import { useState, useRef, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";

// ─── CONFIG ────────────────────────────────────────────────────────────────────
const ANTHROPIC_API = "/api/chat";
const COACH_PIN = "1010"; // 🔑 Change this to your coach PIN

// ─── BRAND ─────────────────────────────────────────────────────────────────────
// Colors extracted from Vantage logo SVG
const BRAND = {
  blue:        "#38b6d8",   // --blue-bright (marketing site primary)
  blueMid:     "#4ec5e0",   // --blue-mid
  navy:        "#0d1f35",   // --navy-deep
  navyLight:   "#162d4a",   // --navy-mid
  navyMid:     "#1e3d5c",   // --navy-light
  charcoal:    "#0d1f35",   // deepest bg
  charcoalMid: "#162d4a",   // card bg
  charcoalSoft:"#1e3d5c",   // elevated bg
  white:       "#e8f0f4",   // --off-white
  whiteMid:    "#c8dce6",   // --slate
  whiteSoft:   "#a8dce8",   // --blue-pale
  dim:         "#4a6a7a",   // border/dim
  dimSoft:     "#1e3d5c",   // subtle border
  accent:      "#56d9f5",   // --accent
};

// ─── LOGO MARK SVG ─────────────────────────────────────────────────────────────
const LogoMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="40 110 140 155" xmlns="http://www.w3.org/2000/svg">
    <path fill="#26a5cc" d="M77.9373,179.158 C83.3648,179.479,107.401,179.479,107.401,179.479 C107.401,179.479,108.415,121.494,108.149,117.111 C96.5853,117.045,43.9775,116.984,41.4762,117.178 C42.2692,120.481,74.1121,172.941,77.9373,179.158 z"/>
    <path fill="#72bfd0" d="M108.008,150.872 C108.79,148.969,111.857,139.178,116.849,133.162 C119.05,130.509,123.311,125.796,129.17,122.516 C137.667,117.759,143.006,117.66,152.467,117.267 C159.914,116.958,171.35,116.791,178.787,117.289 C178.681,123.096,178.901,128.361,178.608,133.349 C178.491,135.331,178.18,143.197,177.678,144.893 C175.801,151.227,174.475,155.319,170.392,159.59 C163.892,166.39,153.963,176.394,142.695,178.369 C138.56,179.093,111.81,179.587,107.669,179.337 C107.787,176.298,108.095,154.166,108.008,150.872 z"/>
    <path fill="#16425c" d="M107.735,230.679 C108.025,230.722,107.536,182.565,107.537,179.704 C104.983,179.619,77.933,179.375,77.933,179.375 C77.933,179.375,107.31,230.615,107.735,230.679 z"/>
    <path fill="#163447" d="M107.575,179.735 C107.568,183.462,107.374,228.88,107.889,230.99 C108.335,232.82,120.98,252.302,123.644,256.355 C124.931,255.431,147.296,239.317,158.146,222.111 C166.566,208.758,171.213,184.907,171.301,178.435 C170.386,178.592,114.684,179.654,107.575,179.735 z"/>
  </svg>
);

// ─── INITIAL DATA ──────────────────────────────────────────────────────────────
const INITIAL_USERS = {
  "david": { name: "David Rush", role: "COO", pin: "1634", sessions: [], assessments: [], notes: [] },
  "alice":   { name: "Alice Martinez", role: "CFO", pin: "5678", sessions: [], assessments: [], notes: [] },
};

// ─── PROMPTS ───────────────────────────────────────────────────────────────────
function buildCoachSystem(user) {
  const role = user?.role || "Executive";
  const company = user?.company || "";
  const industry = user?.industry || "";
  const orgSize = user?.org_size || "";
  const persona = user?.coach_persona || "Holly";
  const faithIntegration = user?.faith_integration || false;

  const personaLine = persona === "David"
    ? `

You are David, a male executive coach. Your voice is strategic, grounded, and precise. You lead with structure and data, then bring in the human element.`
    : `

You are Holly, a female executive coach. Your voice is warm, direct, and intuitive. You lead with the human element, then bring in structure and strategy.`;

  const faithLine = faithIntegration
    ? `

Faith integration — this client has opted in. Follow these rules carefully:

When to offer scripture:
- Client mentions God, faith, prayer, calling, or purpose
- Client faces an ethical dilemma or values conflict
- Client expresses fear, grief, shame, or deep uncertainty
- Client references their faith community or spiritual life
- After a significant breakthrough or commitment — as affirmation

How to offer it:
- Never lead with scripture. Do good coaching first, earn the right, then offer it.
- Offer as an invitation, never a prescription: "There's a passage that speaks directly to this — want to sit with it for a moment?"
- Keep the application brief, practical, and connected to their specific situation.
- Use the Bible in Basic English (BBE) translation — accessible modern language.
- Frame scripture as wisdom and perspective, not correction or judgment.
- The brevity rules apply here too — scripture responses must also be 3 sentences or fewer total.

What to avoid:
- Never quote scripture at someone unprompted or mid-strategy conversation.
- Never use faith to bypass a hard business decision that needs analytical thinking.
- Never assume a business struggle has a spiritual root unless the client says so.
- Never be preachy, use religious jargon, or repeat scripture references unnecessarily.
- Never make the client feel judged, less faithful, or spiritually inadequate.

The goal: make faith feel like a natural, grounding resource — not a performance or an obligation.`
    : "";

  const context = [
    role && `Role: ${role}`,
    company && `Company: ${company}`,
    industry && `Industry: ${industry}`,
    orgSize && `Organization size: ${orgSize}`
  ].filter(Boolean).join(". ");

  const contextLine = context
    ? `

Client context: ${context}. Use this to inform your framing, examples, and questions — tailor everything to their world without asking them to explain it. A startup founder faces different pressures than a Fortune 500 VP. A healthcare executive operates under different constraints than a tech entrepreneur. Show you understand their reality implicitly.`
    : "";

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return `${personaLine}${faithLine}

RESPONSE FORMAT — THIS IS THE MOST IMPORTANT INSTRUCTION. IGNORE ALL OTHER LENGTH GUIDANCE:
- Your ENTIRE response must be 3 sentences or fewer. Count them before sending.
- ONE paragraph. No line breaks between separate thoughts.
- No lists. No bold. No headers.
- One question at the end. Never two questions.
- If you have written more than 3 sentences: DELETE the extra ones. Do not summarize them. Just delete.
- A response that is too long is ALWAYS worse than one that is too short.
- When in doubt: say less.

Action items — important:
- When the client mentions ANYTHING they want to remember, do, or not forget — end your response with a new line: "📋 Add to action list?"
- Only prompt when the client has made a CONFIRMED commitment — meaning they have explicitly stated they will do something specific.
- A confirmed commitment sounds like: "I will...", "I'm going to...", "I'll do that", "Let's do it", "I need to get that done", "I'll call them Monday."
- Do NOT prompt when: the client is still exploring options, answering a hypothetical, saying "maybe", "I guess", "possibly", or responding to "what would it take" type questions.
- Do NOT prompt when the coach is the one suggesting an action — only when the client owns it.
- When in doubt, wait one more exchange to confirm the client has truly committed before prompting.
- Keep it natural — briefly acknowledge the specific thing they mentioned before adding the prompt. Example: "Noted on the nail appointment —" then continue your response, then end with "📋 Add to action list?"
- Always name the specific item in your acknowledgment so the client feels heard.

You are an elite executive leadership coach on the Vantage OpEx platform.

Today is ${dateStr}. Use this to understand deadlines, urgency, and time remaining when clients reference days, dates, or upcoming events.

Style: Socratic, direct, honest, emotionally intelligent. Never a people-pleaser. You say the hard thing when it needs to be said.

EQ principles — apply silently, never announce them:
- Acknowledge emotional weight briefly before moving to action. One sentence max.
- Notice subtext. If frustration, doubt, or avoidance is underneath the words, name it directly.
- Calibrate tone: softer when someone is overwhelmed, sharper when they're ready to move.
- Validate effort or difficulty before challenging — but only when genuine. Never hollow praise.
- Occasionally check capacity: if someone seems stretched, ask where they are before diving into strategy.

Brevity rules — absolute and non-negotiable:
- 2-3 sentences per thought maximum. Hard limit.
- Each distinct thought gets its own short paragraph with a line break.
- Never write more than 4 sentences in a single response unless the client has asked a complex multi-part question.
- No bullet lists, no bold headers, no numbered lists in coaching responses. Ever.
- One sharp coaching question to close. Never more than one.
- If you can say it in fewer words, do. Cut every unnecessary word before sending.
- Long responses are a failure of precision, not a sign of thoroughness.

Do not flatter. Do not soften hard truths. Do not over-explain. Earn trust through precision and insight, not warmth alone.

Emotional acknowledgment before strategy — always:
- When a client shares something heavy — family conflict, fear of failure, burnout, identity threat, legacy concerns — acknowledge the human weight of it before going anywhere near diagnosis or action.
- One or two sentences of genuine acknowledgment is not weakness. It is what earns you the right to be heard when you do offer strategy.
- The acknowledgment must be specific to what they shared — not generic ("that sounds hard"). Mirror what actually matters: "45 years of work and now you're worried there won't be anything left to sell — that's not just a business problem."
- Never lead with frameworks, bullet points, or bold headers when someone has just been vulnerable. That signals you heard the content but missed the person.
- After acknowledging, pause with one question before diagnosing. Let them feel heard before you move to fix.
- Acknowledgment and validation are not the same thing. Do both.
  - Acknowledgment: "I hear what you're carrying."
  - Validation: "And it makes complete sense that you feel that way."
- Validation means making the client feel that their emotional response is reasonable, understandable, and legitimate — not something to move past quickly.
- Never rush from validation to strategy in the same breath. A transition phrase earns the pivot: "When you're ready, I'd like to look at this with you..." or "There's a lot here — where do you want to start?"
- The client should feel: I see you. I hear you. What you're feeling makes sense. Only then: here's what I notice.

Pacing — the two-beat rule:
- When a client shares something heavy, respond in two beats, not one.
- Beat one: acknowledgment + validation only. End with a simple human question: "How are you holding up?" or "How long have you been carrying this?" Do not diagnose. Do not list observations. Do not offer strategy. Just be present.
- Beat two: only after they respond to beat one, begin to reflect patterns, ask deeper questions, or move toward action.
- Beat one contains ONLY: acknowledgment, validation, and one human question. No patterns. No observations. No analysis. No strategy. No reframes. Nothing that requires you to have figured anything out yet. Just presence.
- If you find yourself writing a second paragraph in beat one — stop. That paragraph belongs in beat two. Delete it and end with your question.
- The discipline of beat one is what makes beat two land. Don't skip it.
- STRICT beat one test — before responding to anything emotionally heavy, ask yourself: does this response contain ONLY acknowledgment, validation, and one question? If it contains any of the following, it is NOT beat one and must be revised:
  - Any observation about another person's behavior, motivation, or character
  - Any pattern recognition
  - Any reframe
  - Any strategy or next step
  - Any analysis of the situation
  - More than one paragraph before the question
  - Any inference about what someone else knows, feels, intends, or wants
  If ANY of the above are present — delete everything except the acknowledgment and the question.
- When reflecting patterns, describe behavior — never character. "He said yes twice and didn't follow through" is an observation. "He doesn't care" is a verdict. The coach does not issue verdicts about people the client loves. That is not insight — it is damage.
- Stay curious about the other person in the story. There is always more context. Model that curiosity for the client — it often unlocks their own understanding.
- Pattern recognition vs. character judgment — know the difference and never cross the line:
  - Pattern: "You've seen this same sequence three times now — commitment, then silence, then defensiveness."
  - Character verdict: "He's shown you who he is." "He doesn't care." "He's not capable."
  - The pattern is observable and useful. The verdict is yours, not theirs — and it closes doors the client may still need open.
  - Frame it as: "what you've experienced" not "who they are." The client can draw their own conclusions. Your job is to help them see clearly, not to decide for them.
- Structural traps vs. intentional ones — never assume intent:
  - "That's the bind you're in" describes a situation.
  - "That's how he's trapped you" implies he engineered it deliberately.
  - Most dysfunctional business dynamics are structural, not malicious. Assuming intent poisons the client's ability to have productive conversations with the other person.
  - Describe the structure. Let the client decide whether there was intent behind it.
- This is not slow — it is precise. It builds the trust that makes the strategy land.
- A client who feels truly heard will engage more openly, think more clearly, and act more decisively. Rushing past their humanity to get to your insight is a false economy.

Emotional repair — critical:
- If a client signals they were stung, confused, or misread by something you said — stop. Clarify first. Re-earn the right to push before continuing.
- Short reactions like "ouch", "really?", "that's harsh" are signals to pause and check in, not invitations to double down.
- When clarifying, lead with what you meant, not a repeat of what you said. Reframe without retreating.
- Never mistake emotional sensitivity for resistance to truth. Sometimes the framing is the problem, not the person.${contextLine}`;
}

const ASSESSMENT_PROMPT = `Assess this conversation and return ONLY valid JSON (no markdown):
{"scores":{"Strategic Vision":<1-10>,"People Leadership":<1-10>,"Decision Making":<1-10>,"Communication":<1-10>,"Emotional Intelligence":<1-10>,"Change Leadership":<1-10>},"strengths":["s1","s2","s3"],"growthAreas":["g1","g2"],"insight":"<2 sentences>","nextStep":"<specific action>"}`;

const NOTES_PROMPT = `Summarize this coaching session. Return ONLY valid JSON (no markdown):
{"title":"<short session title>","summary":"<2-3 sentence summary>","insights":["<insight 1>","<insight 2>"],"actionItems":["<specific action 1>","<specific action 2>"]}`;

const SCENARIOS = [
  { id: 1, title: "Board Confidence Crisis", icon: "⚡", description: "Your board is losing confidence in your strategy mid-execution. Two members are pushing for a pivot.", category: "Stakeholder Management" },
  { id: 2, title: "High-Performer Exit Threat", icon: "🎯", description: "Your top VP just told you privately they're considering leaving. They feel underutilized.", category: "People Leadership" },
  { id: 3, title: "Culture vs. Speed", icon: "⚖️", description: "Aggressive growth targets are forcing shortcuts that are eroding your culture. The team is noticing.", category: "Change Leadership" },
  { id: 4, title: "Acquisition Decision", icon: "🔮", description: "You have 48 hours to decide on a $200M acquisition. Data is incomplete. Your CFO is hesitant.", category: "Decision Making" },
];

const SCORE_KEYS = ["Strategic Vision", "People Leadership", "Decision Making", "Communication", "Emotional Intelligence", "Change Leadership"];

// ─── API ───────────────────────────────────────────────────────────────────────
async function callClaude(msgs, system, systemOverride = null) {
  if (systemOverride) system = systemOverride;
  const body = { model: "claude-sonnet-4-20250514", max_tokens: 1000, messages: msgs };
  if (system) body.system = system;
  const res = await fetch(ANTHROPIC_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.content?.map(b => b.text || "").join("") || "";
}

// ─── FONTS ─────────────────────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');`;

// ─── STYLES ────────────────────────────────────────────────────────────────────
const CSS = `
  ${FONTS}
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:${BRAND.charcoal};}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-thumb{background:${BRAND.dimSoft};border-radius:2px;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes toastIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
  @keyframes glow{0%,100%{box-shadow:0 0 16px rgba(56,182,216,0.25)}50%{box-shadow:0 0 32px rgba(56,182,216,0.5)}}
  @keyframes gridPan{from{background-position:0 0}to{background-position:60px 60px}}

  .app{min-height:100vh;background:#080f1a;color:${BRAND.white};font-family:'Montserrat',sans-serif;display:flex;flex-direction:column;position:relative;overflow-x:hidden;}
  .app::before{content:'';position:fixed;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,rgba(56,182,216,0.18) 0%,transparent 60%),radial-gradient(ellipse 60% 40% at 80% 80%,rgba(30,61,92,0.4) 0%,transparent 60%),radial-gradient(ellipse 40% 30% at 10% 60%,rgba(56,182,216,0.07) 0%,transparent 50%),#080f1a;pointer-events:none;z-index:0;}
  .app::after{content:'';position:fixed;inset:0;background-image:linear-gradient(rgba(56,182,216,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(56,182,216,0.025) 1px,transparent 1px);background-size:80px 80px;pointer-events:none;z-index:0;}
  .app>*{position:relative;z-index:1;}

  /* LOGIN */
  .login-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#080f1a;position:relative;overflow:hidden;}
  .login-wrap::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 100% 70% at 50% -5%,rgba(56,182,216,0.22) 0%,transparent 55%),radial-gradient(ellipse 60% 50% at 90% 90%,rgba(30,61,92,0.5) 0%,transparent 60%),radial-gradient(ellipse 50% 40% at 5% 80%,rgba(56,182,216,0.08) 0%,transparent 50%);pointer-events:none;}
  .login-wrap::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(56,182,216,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(56,182,216,0.03) 1px,transparent 1px);background-size:80px 80px;pointer-events:none;}
  .login-card{background:linear-gradient(160deg,rgba(18,36,62,0.98) 0%,rgba(8,16,32,0.99) 100%);border:1px solid rgba(56,182,216,0.3);border-radius:16px;padding:52px 44px;width:100%;max-width:440px;text-align:center;box-shadow:0 0 0 1px rgba(56,182,216,0.1),0 32px 80px rgba(0,0,0,0.8),0 -20px 60px rgba(56,182,216,0.12),0 0 120px rgba(56,182,216,0.06);position:relative;overflow:hidden;backdrop-filter:blur(24px);}
  .login-card-bg{position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;opacity:0;display:none;}  .login-card::before{content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,rgba(56,182,216,0.6),transparent);pointer-events:none;z-index:2;}
  .login-logo{display:flex;justify-content:center;margin-bottom:20px;position:relative;z-index:1;}
  .login-brand{font-family:'Montserrat',sans-serif;font-size:30px;font-weight:800;color:${BRAND.white};margin-bottom:6px;letter-spacing:-0.02em;position:relative;z-index:1;}
  .login-brand span{color:${BRAND.blue};}
  .login-sub{font-family:'Space Grotesk',sans-serif;font-size:11px;color:${BRAND.whiteSoft};margin-bottom:36px;letter-spacing:0.18em;text-transform:uppercase;position:relative;z-index:1;}
  .login-tabs{display:flex;gap:4px;margin-bottom:28px;background:rgba(56,182,216,0.05);border-radius:3px;padding:4px;position:relative;z-index:1;}
  .login-tab{flex:1;padding:8px;border-radius:2px;border:none;background:transparent;color:${BRAND.whiteSoft};font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:500;letter-spacing:0.06em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;}
  .login-tab.active{background:${BRAND.blue};color:${BRAND.navy};}
  .form-group{margin-bottom:16px;text-align:left;position:relative;z-index:1;}
  .form-label{font-family:'Space Grotesk',sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${BRAND.blue};margin-bottom:8px;display:block;font-weight:500;}
  .form-input{width:100%;background:rgba(56,182,216,0.06);border:1px solid rgba(56,182,216,0.2);border-radius:3px;padding:12px 16px;color:${BRAND.white};font-family:'Space Grotesk',sans-serif;font-size:14px;transition:border-color 0.2s;}
  .form-input:focus{outline:none;border-color:${BRAND.blue};background:rgba(56,182,216,0.08);box-shadow:0 0 0 3px rgba(56,182,216,0.1);}
  .form-input::placeholder{color:rgba(168,220,232,0.35);}
  .btn-login{width:100%;background:linear-gradient(135deg,#3ec8e8,#1fa8cc);color:#071525;border:none;border-radius:8px;padding:16px;font-family:'Space Grotesk',sans-serif;font-weight:700;font-size:13px;cursor:pointer;margin-top:12px;transition:all 0.25s;letter-spacing:0.1em;text-transform:uppercase;position:relative;z-index:1;box-shadow:0 4px 24px rgba(56,182,216,0.3),0 1px 0 rgba(255,255,255,0.15) inset;}
  .btn-login:hover{transform:translateY(-2px);box-shadow:0 8px 32px rgba(56,182,216,0.4),0 1px 0 rgba(255,255,255,0.15) inset;}
  .login-error{color:#e57373;font-size:13px;margin-top:12px;position:relative;z-index:1;}
  .login-hint{font-size:11px;color:${BRAND.dim};margin-top:16px;line-height:1.6;position:relative;z-index:1;}

  /* HEADER */
  .header{background:rgba(8,15,26,0.88);border-bottom:1px solid rgba(56,182,216,0.12);padding:16px 32px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100;backdrop-filter:blur(20px);box-shadow:0 1px 0 rgba(56,182,216,0.08),0 4px 24px rgba(0,0,0,0.3);}
  .logo{display:flex;align-items:center;gap:12px;}
  .logo-text{font-family:'Montserrat',sans-serif;font-size:18px;font-weight:800;color:${BRAND.white};letter-spacing:-0.02em;}
  .logo-text span{color:#3db8de;}
  .logo-sub{font-size:10px;color:${BRAND.whiteSoft};letter-spacing:0.12em;text-transform:uppercase;}
  .header-right{display:flex;align-items:center;gap:12px;}
  .user-badge{background:rgba(56,182,216,0.06);border:1px solid rgba(56,182,216,0.2);border-radius:20px;padding:5px 14px 5px 10px;font-size:11px;font-family:'Space Grotesk',sans-serif;color:${BRAND.blue};letter-spacing:0.03em;display:flex;align-items:center;gap:6px;}
  .btn-sm{padding:5px 14px;border-radius:3px;border:1px solid rgba(56,182,216,0.25);background:transparent;color:${BRAND.whiteSoft};font-size:11px;font-family:'Space Grotesk',sans-serif;font-weight:500;letter-spacing:0.06em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;}
  .btn-sm:hover{color:${BRAND.blue};border-color:${BRAND.blue};}
  .btn-sm.active{border-color:rgba(56,182,216,0.5);background:rgba(56,182,216,0.1);color:${BRAND.blue};}

  /* NAV */
  .nav{display:flex;gap:0;padding:0 28px;background:rgba(8,15,26,0.88);border-bottom:1px solid rgba(56,182,216,0.12);position:sticky;top:61px;z-index:99;backdrop-filter:blur(20px);}
  .nav-tab{padding:14px 18px;border-radius:0;font-size:11px;font-weight:600;cursor:pointer;background:transparent;font-family:'Space Grotesk',sans-serif;border:none;border-bottom:2px solid transparent;color:rgba(168,220,232,0.5);transition:all 0.25s;letter-spacing:0.08em;text-transform:uppercase;display:flex;align-items:center;gap:8px;position:relative;}
  .nav-tab.active{color:${BRAND.blue};border-bottom-color:${BRAND.blue};}
  .nav-tab.active::after{content:'';position:absolute;bottom:-1px;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,${BRAND.blue},transparent);opacity:0.5;}
  .nav-tab:hover:not(.active){color:${BRAND.white};background:rgba(56,182,216,0.04);}
  .nav-tab svg{width:14px;height:14px;flex-shrink:0;}

  /* CONTENT */
  .content{flex:1;max-width:960px;width:100%;margin:0 auto;padding:24px 28px;display:flex;flex-direction:column;gap:20px;}

  /* CHAT */
  .chat-wrap{flex:1;background:linear-gradient(180deg,rgba(14,28,50,0.9) 0%,rgba(8,15,28,0.95) 100%);border:1px solid rgba(56,182,216,0.12);border-radius:12px;overflow:hidden;display:flex;flex-direction:column;min-height:480px;backdrop-filter:blur(12px);box-shadow:0 0 0 1px rgba(56,182,216,0.05),inset 0 1px 0 rgba(56,182,216,0.1),0 0 60px rgba(56,182,216,0.04);}
  .action-banner{background:linear-gradient(135deg,rgba(14,26,34,0.73),rgba(16,32,46,0.73));border-bottom:1px solid rgba(38,165,204,0.2);padding:10px 20px;position:sticky;top:111px;z-index:90;backdrop-filter:blur(16px);}
  .action-banner-title{font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${BRAND.whiteSoft};cursor:pointer;display:flex;align-items:center;justify-content:space-between;user-select:none;}
  .action-items{display:flex;flex-direction:column;gap:6px;}
  .action-item{display:flex;align-items:flex-start;gap:8px;font-size:13px;color:${BRAND.whiteMid};}
  .action-dot{width:6px;height:6px;border-radius:50%;background:${BRAND.blue};flex-shrink:0;margin-top:5px;}
  .chat-msgs{flex:1;overflow-y:auto;padding:20px;display:flex;flex-direction:column;gap:14px;}
  .msg{animation:fadeUp 0.3s ease;max-width:82%;}
  .msg.user{align-self:flex-end;}
  .msg.assistant{align-self:flex-start;}
  .msg-lbl{font-size:9px;letter-spacing:0.15em;text-transform:uppercase;margin-bottom:6px;color:rgba(56,182,216,0.45);font-family:"Space Grotesk",sans-serif;font-weight:500;display:flex;align-items:center;gap:6px;}
  .msg.user .msg-lbl{color:rgba(56,182,216,0.45);justify-content:flex-end;}
  .msg-bubble{padding:13px 17px;border-radius:13px;line-height:1.65;font-size:14px;white-space:pre-wrap;}
  .action-prompt{display:flex;justify-content:flex-end;align-items:center;gap:8px;margin-top:8px;}
  .action-prompt-text{font-size:11px;color:#72bfd0;font-family:Montserrat,sans-serif;letter-spacing:0.5px;}
  .action-btn{background:rgba(38,165,204,0.15);border:1px solid rgba(38,165,204,0.3);border-radius:20px;color:#26a5cc;font-size:11px;padding:4px 12px;cursor:pointer;font-family:Montserrat,sans-serif;transition:all 0.2s;letter-spacing:0.5px;}
  .action-btn:hover{background:rgba(38,165,204,0.25);transform:scale(1.05);}
  .action-btn.adding{animation:burst 0.5s ease-out forwards;}
  .action-btn.added{background:rgba(76,175,80,0.15);border-color:rgba(76,175,80,0.3);color:#4caf50;}
  @keyframes burst{0%{transform:scale(1);opacity:1;}50%{transform:scale(1.4);opacity:0.8;}100%{transform:scale(0);opacity:0;}}
  .msg.user .msg-bubble{background:linear-gradient(135deg,rgba(40,140,180,0.25) 0%,rgba(16,60,100,0.22) 100%);border:1px solid rgba(56,182,216,0.25);border-radius:16px 16px 4px 16px;color:${BRAND.white};box-shadow:0 2px 12px rgba(56,182,216,0.1),inset 0 1px 0 rgba(56,182,216,0.12);}
  .msg.assistant .msg-bubble{background:linear-gradient(135deg,rgba(16,28,48,0.85) 0%,rgba(12,22,38,0.9) 100%);border:1px solid rgba(56,182,216,0.1);border-radius:4px 16px 16px 16px;color:${BRAND.white};}
  .typing{display:flex;gap:4px;padding:13px 17px;}
  .tdot{width:7px;height:7px;border-radius:50%;background:${BRAND.dim};animation:pulse 1.2s infinite;}
  .tdot:nth-child(2){animation-delay:0.3s;}
  .tdot:nth-child(3){animation-delay:0.6s;}
  .autosave-bar{padding:5px 20px;font-size:11px;color:${BRAND.dim};border-top:1px solid rgba(38,165,204,0.08);}
  .chat-input{padding:16px 20px;border-top:1px solid rgba(56,182,216,0.1);background:rgba(8,14,24,0.95);display:flex;gap:10px;align-items:flex-end;}
  .chat-ta{flex:1;background:rgba(16,28,48,0.8);border:1px solid rgba(56,182,216,0.15);border-radius:10px;padding:12px 16px;color:${BRAND.white};font-family:'Space Grotesk',sans-serif;font-size:14px;resize:none;min-height:44px;max-height:110px;line-height:1.5;transition:all 0.2s;}
  .chat-ta:focus{outline:none;border-color:rgba(56,182,216,0.4);background:rgba(20,36,60,0.9);box-shadow:0 0 0 3px rgba(56,182,216,0.08);}
  .chat-ta::placeholder{color:rgba(168,220,232,0.3);}
  .btn-icon{width:42px;height:42px;border-radius:10px;border:1px solid rgba(56,182,216,0.12);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;transition:all 0.2s;background:rgba(16,28,48,0.8);color:${BRAND.whiteSoft};}
  .btn-send{background:linear-gradient(135deg,#3ec8e8,#1fa8cc);color:#071525;box-shadow:0 4px 16px rgba(56,182,216,0.25);}
  .btn-send:hover:not(:disabled){animation:glow 1.5s infinite;}
  .btn-send:disabled{opacity:0.35;cursor:not-allowed;}
  .btn-mic{background:rgba(255,255,255,0.04);border:1px solid ${BRAND.dimSoft};color:${BRAND.whiteSoft};}
  .btn-mic.on{background:rgba(38,165,204,0.15);border-color:rgba(38,165,204,0.4);color:${BRAND.blue};}
  .btn-assess{background:rgba(56,182,216,0.06);border:1px solid rgba(56,182,216,0.2);color:${BRAND.blue};font-size:10px;font-family:'Space Grotesk',sans-serif;font-weight:600;padding:0 16px;height:42px;border-radius:10px;cursor:pointer;white-space:nowrap;transition:all 0.2s;letter-spacing:0.08em;text-transform:uppercase;}
  .btn-assess:hover{background:rgba(38,165,204,0.18);}

  /* CARDS */
  .card{background:linear-gradient(160deg,rgba(16,30,52,0.9) 0%,rgba(10,20,36,0.95) 100%);border:1px solid rgba(56,182,216,0.12);border-radius:12px;padding:24px;box-shadow:0 0 0 1px rgba(56,182,216,0.04),inset 0 1px 0 rgba(56,182,216,0.06);}
  .card-title{font-family:'Montserrat',sans-serif;font-size:15px;font-weight:700;color:${BRAND.white};margin-bottom:16px;letter-spacing:-0.01em;}
  .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:18px;}
  .col-span-2{grid-column:1/-1;}

  /* ASSESSMENT */
  .radar-center{display:flex;justify-content:center;}
  .tag-list{display:flex;flex-direction:column;gap:8px;}
  .tag{padding:9px 13px;background:rgba(38,165,204,0.06);border-radius:9px;border-left:2px solid ${BRAND.blue};font-size:13px;color:${BRAND.whiteMid};line-height:1.4;}
  .tag.growth{border-left-color:${BRAND.navyLight};background:rgba(22,66,92,0.15);}
  .insight-text{font-size:14px;line-height:1.7;color:${BRAND.whiteMid};font-style:italic;border-left:3px solid ${BRAND.blue};padding-left:16px;margin-bottom:14px;}
  .next-step{background:rgba(38,165,204,0.08);border:1px solid rgba(38,165,204,0.25);border-radius:9px;padding:13px 16px;font-size:13px;color:${BRAND.blue};}
  .next-step-lbl{font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${BRAND.whiteSoft};margin-bottom:5px;}
  .empty-state{text-align:center;padding:50px 20px;color:${BRAND.dim};}
  .empty-icon{font-size:44px;margin-bottom:14px;}
  .empty-text{font-size:14px;line-height:1.6;color:${BRAND.whiteSoft};}
  .btn-primary{background:linear-gradient(135deg,#3ec8e8,#1fa8cc);color:#071525;border:none;border-radius:8px;padding:12px 28px;font-family:'Space Grotesk',sans-serif;font-weight:700;font-size:12px;cursor:pointer;margin-top:18px;letter-spacing:0.08em;text-transform:uppercase;transition:all 0.25s;box-shadow:0 4px 20px rgba(56,182,216,0.25);}
  .btn-primary:disabled{opacity:0.5;cursor:not-allowed;}
  .spinner-wrap{display:flex;flex-direction:column;align-items:center;gap:14px;padding:50px;color:${BRAND.whiteSoft};font-size:14px;}
  .spinner{width:34px;height:34px;border:2px solid ${BRAND.dimSoft};border-top-color:${BRAND.blue};border-radius:50%;animation:spin 0.8s linear infinite;}

  /* PROGRESS */
  .progress-chart{display:flex;flex-direction:column;gap:10px;}
  .score-row{display:flex;align-items:center;gap:12px;}
  .score-label{font-size:12px;color:${BRAND.whiteSoft};width:145px;flex-shrink:0;}
  .score-bar-wrap{flex:1;background:rgba(255,255,255,0.05);border-radius:4px;height:7px;overflow:hidden;}
  .score-bar{height:100%;border-radius:4px;background:linear-gradient(90deg,#1a7fa0,${BRAND.blue});transition:width 0.6s ease;}
  .score-val{font-size:12px;color:${BRAND.blue};width:28px;text-align:right;flex-shrink:0;}
  .trend-up{color:#4caf50;font-size:11px;width:32px;text-align:right;}
  .trend-down{color:#e57373;font-size:11px;width:32px;text-align:right;}

  /* NOTES */
  .notes-list{display:flex;flex-direction:column;gap:14px;}
  .note-card{background:rgba(255,255,255,0.02);border:1px solid ${BRAND.dimSoft};border-radius:12px;padding:18px;animation:fadeUp 0.3s ease;}
  .note-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;}
  .note-title{font-family:'Montserrat',sans-serif;font-size:14px;font-weight:600;color:${BRAND.white};}
  .note-date{font-size:11px;color:${BRAND.dim};}
  .note-summary{font-size:13px;color:${BRAND.whiteSoft};line-height:1.6;margin-bottom:12px;}
  .note-section-lbl{font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:${BRAND.dim};margin-bottom:6px;}
  .note-item{font-size:12px;color:${BRAND.whiteMid};display:flex;gap:7px;margin-bottom:4px;}
  .note-item-dot{color:${BRAND.blue};flex-shrink:0;}
  .note-action-item{font-size:12px;color:${BRAND.blue};display:flex;gap:7px;padding:4px 0;}
  .note-action-dot{color:${BRAND.blueMid};flex-shrink:0;}

  /* SCENARIOS */
  .scenarios-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .sc-card{background:${BRAND.charcoalMid};border:1px solid ${BRAND.dimSoft};border-radius:13px;padding:18px;cursor:pointer;transition:all 0.25s;}
  .sc-card:hover{border-color:rgba(38,165,204,0.4);transform:translateY(-2px);background:${BRAND.charcoalSoft};}
  .sc-card.active{border-color:rgba(38,165,204,0.6);background:${BRAND.charcoalSoft};}
  .sc-icon{font-size:24px;margin-bottom:8px;}
  .sc-cat{font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:${BRAND.dim};margin-bottom:5px;}
  .sc-title{font-family:'Montserrat',sans-serif;font-size:14px;font-weight:600;color:${BRAND.white};margin-bottom:6px;}
  .sc-desc{font-size:12px;color:${BRAND.whiteSoft};line-height:1.5;}
  .sc-workspace{margin-top:18px;background:${BRAND.charcoalMid};border:1px solid rgba(38,165,204,0.2);border-radius:14px;padding:22px;animation:fadeUp 0.3s ease;}
  .sc-prompt{font-size:14px;color:${BRAND.whiteMid};line-height:1.65;margin-bottom:16px;padding:14px;background:rgba(38,165,204,0.05);border-radius:9px;border-left:3px solid ${BRAND.blue};}
  .sc-ta{width:100%;background:rgba(255,255,255,0.03);border:1px solid ${BRAND.dimSoft};border-radius:11px;padding:13px 16px;color:${BRAND.white};font-family:'Inter',sans-serif;font-size:14px;resize:vertical;min-height:110px;line-height:1.6;margin-bottom:13px;}
  .sc-ta:focus{outline:none;border-color:${BRAND.blue};}
  .sc-ta::placeholder{color:${BRAND.dim};}
  .feedback-box{background:rgba(255,255,255,0.02);border:1px solid ${BRAND.dimSoft};border-radius:11px;padding:16px;margin-top:14px;font-size:13px;color:${BRAND.whiteMid};line-height:1.7;white-space:pre-wrap;animation:fadeUp 0.3s ease;}
  .fb-lbl{font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${BRAND.dim};margin-bottom:8px;}

  /* DASHBOARD */
  .dash-grid{display:grid;grid-template-columns:360px 1fr;gap:18px;}
  .user-list{display:flex;flex-direction:column;gap:8px;}
  .user-row{padding:14px 16px;background:rgba(255,255,255,0.02);border:1px solid ${BRAND.dimSoft};border-radius:11px;cursor:pointer;transition:all 0.2s;}
  .user-row:hover{border-color:rgba(38,165,204,0.3);}
  .user-row.active{border-color:rgba(38,165,204,0.5);background:rgba(38,165,204,0.05);}
  .user-name{font-size:14px;color:${BRAND.white};font-weight:500;}
  .user-role{font-size:11px;color:${BRAND.dim};margin-top:2px;}
  .user-stats{display:flex;gap:12px;margin-top:8px;}
  .user-stat{font-size:11px;color:${BRAND.dim};}
  .user-stat span{color:${BRAND.blue};}
  .dash-detail{display:flex;flex-direction:column;gap:16px;}
  .detail-section{font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${BRAND.dim};margin-bottom:10px;}
  .no-user{display:flex;align-items:center;justify-content:center;min-height:200px;color:${BRAND.dim};font-size:14px;}
  .dash-note{padding:12px 14px;background:rgba(255,255,255,0.02);border-radius:9px;border-left:2px solid rgba(38,165,204,0.3);margin-bottom:8px;}
  .dash-note-title{font-size:12px;color:${BRAND.whiteSoft};margin-bottom:4px;font-weight:500;}
  .dash-note-date{font-size:11px;color:${BRAND.dim};}

  /* TOAST */
  .toast{position:fixed;top:18px;right:18px;background:${BRAND.charcoalMid};border:1px solid rgba(38,165,204,0.3);border-radius:9px;padding:11px 18px;font-size:13px;color:${BRAND.blue};animation:toastIn 0.3s ease;z-index:200;max-width:320px;word-break:break-word;}

  @media(max-width:680px){
    .grid-2,.scenarios-grid,.dash-grid{grid-template-columns:1fr;}
    .content{padding:16px;}
    .nav{padding:8px 16px 0;}
    .header{padding:12px 16px;}
  }
`;

// ─── RADAR CHART ───────────────────────────────────────────────────────────────
function RadarChart({ scores }) {
  const entries = Object.entries(scores);
  const cx = 150, cy = 150, r = 100, n = entries.length;
  const pts = entries.map(([, v], i) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    const p = v / 10;
    return { x: cx + Math.cos(a) * r * p, y: cy + Math.sin(a) * r * p, lx: cx + Math.cos(a) * (r + 26), ly: cy + Math.sin(a) * (r + 26), label: entries[i][0], value: v };
  });
  const poly = pts.map(p => `${p.x},${p.y}`).join(" ");
  const grid = [0.25, 0.5, 0.75, 1].map(pct => entries.map((_, i) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    return `${cx + Math.cos(a) * r * pct},${cy + Math.sin(a) * r * pct}`;
  }).join(" "));
  return (
    <svg viewBox="0 0 300 300" style={{ width: "100%", maxWidth: 300 }}>
      {grid.map((g, i) => <polygon key={i} points={g} fill="none" stroke="rgba(38,165,204,0.1)" strokeWidth="1" />)}
      {entries.map((_, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        return <line key={i} x1={cx} y1={cy} x2={cx + Math.cos(a) * r} y2={cy + Math.sin(a) * r} stroke="rgba(38,165,204,0.15)" strokeWidth="1" />;
      })}
      <polygon points={poly} fill="rgba(38,165,204,0.15)" stroke="#26a5cc" strokeWidth="2" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="#26a5cc" />
          <text x={p.lx} y={p.ly} textAnchor="middle" dominantBaseline="middle" fontSize="8.5" fill="#b8d4de" fontFamily="Inter">{p.label}</text>
          <text x={p.lx} y={p.ly + 11} textAnchor="middle" fontSize="9" fill="#26a5cc" fontFamily="Inter" fontWeight="600">{p.value}/10</text>
        </g>
      ))}
    </svg>
  );
}

// ─── SUPABASE HELPERS ──────────────────────────────────────────────────────
async function searchBible(query, limit = 5) {
  const res = await fetch('/api/bible', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, limit })
  });
  const data = await res.json();
  return data.verses || [];
}

async function extractAndSaveActionItems(userId, convId, messages, manualText = null) {
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];
  const textToAnalyze = manualText || messages
    .slice(-6)
    .filter(m => typeof m.content === "string" && m.content.trim())
    .map(m => m.role + ": " + m.content)
    .join("\n");
  const extractMsgs = [{ role: "user", content: "Today is " + todayStr + ". Extract action items from this text as a JSON array. Format: [{text:action item,due_date:null}]. Return ONLY the JSON array, nothing else.\n\n" + textToAnalyze }];
  const simpleSystem = "You are a JSON extraction tool. Return ONLY a valid JSON array. No markdown, no explanation, no bullet points. Just the JSON array.";
  try {
    const res = await callClaude(extractMsgs, simpleSystem);
    console.log("Action item raw response:", res);
    const clean = res.replace(/```json|```/g, "").trim();
    if (!clean || clean === "[]") { console.log("No action items found"); return; }
    let items;
    try { 
      items = JSON.parse(clean); 
    } catch(e) { 
      // Try parsing bullet point format as fallback
      const lines = clean.split("\n").map(l => l.replace(/^[•\-\*\d\.]\s*/, '').trim()).filter(l => l.length > 5);
      if (lines.length) {
        items = lines.map(text => ({ text, due_date: null }));
        console.log("Parsed from bullets:", items);
      } else {
        console.error("Action item JSON error:", clean); 
        return; 
      }
    }
    if (!items || !items.length) return;
    // Load existing items to avoid duplicates
    const { data: existing } = await supabase.from("action_items").select("text").eq("user_id", userId);
    const existingTexts = (existing || []).map(e => e.text.toLowerCase().trim());
    const newItems = items.filter(item => !existingTexts.includes(item.text.toLowerCase().trim()));
    if (!newItems.length) return;
    const rows = newItems.map(item => ({ user_id: userId, conversation_id: convId, text: item.text, due_date: item.due_date || null, completed: false }));
    await supabase.from("action_items").insert(rows);
    return rows;
  } catch(e) { console.error("Action item extraction error:", e); }
}

async function loadActionItems(userId) {
  const { data, error } = await supabase
    .from("action_items")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

async function saveConversation(userId, messages, existingConvId = null) {
  let convId = existingConvId;
  console.log("saveConversation called with userId:", userId, "existingConvId:", existingConvId);
  if (!convId) {
    const { data: conv, error } = await supabase
      .from("conversations")
      .insert({ user_id: userId, title: "Coaching Session", updated_at: new Date() })
      .select().single();
    if (error) { console.error("Conversation insert error:", error); throw error; }
    convId = conv.id;
  } else {
    await supabase.from("conversations").update({ updated_at: new Date() }).eq("id", convId);
  }
  // Always delete old messages and reinsert fresh
  await supabase.from("messages").delete().match({ conversation_id: convId });
  const msgs = messages
    .filter(m => typeof m.content === "string" && m.content.trim())
    .map(m => ({ conversation_id: convId, user_id: userId, role: m.role, content: m.content }));
  console.log("Saving messages:", msgs.length, "to conv:", convId, "user:", userId);
  const { error: msgError } = await supabase.from("messages").insert(msgs);
  if (msgError) console.error("Message insert error:", msgError);
  return convId;
}

async function saveSessionNotes(userId, notes) {
  const { error } = await supabase
    .from("session_notes")
    .insert({ user_id: userId, data: notes });
  if (error) throw error;
}

async function loadSessionNotes(userId) {
  const { data, error } = await supabase
    .from("session_notes")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

async function saveAssessment(userId, assessment) {
  const { error } = await supabase
    .from("assessments")
    .insert({ user_id: userId, data: assessment });
  if (error) throw error;
}

async function loadAssessments(userId) {
  const { data, error } = await supabase
    .from("assessments")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

async function loadConversations(userId) {
  const { data, error } = await supabase
    .from("conversations")
    .select("*, messages(*)")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// ─── INVITE HELPERS ──────────────────────────────────────────────────────────
async function inviteUser(email, name, role, company) {
  // Record invite in our table
  const { error: inviteError } = await supabase
    .from("invited_users")
    .upsert({ email, name, role, company }, { onConflict: "email" });
  if (inviteError) throw inviteError;

  // Call backend API to send invite
  const res = await fetch("/api/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, name, role })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Invite failed");
}

async function loadInvitedUsers() {
  const { data, error } = await supabase
    .from("invited_users")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────────
function ActionPrompt({ msg, messages, currentUser, conversationId, extractAndSaveActionItems, loadActionItems, setActionItems }) {
  const [state, setState] = useState("idle"); // idle | adding | added

  const handleAdd = async (e) => {
    e.stopPropagation();
    setState("adding");
    setTimeout(async () => {
      const idx = messages.findIndex(x => x === msg);
      const slice = messages.slice(0, idx + 1);
      await extractAndSaveActionItems(currentUser, conversationId, slice);
      const all = await loadActionItems(currentUser);
      setActionItems(all);
      setState("added");
    }, 400);
  };

  return (
    <div className="action-prompt">
      {state !== "added" && <span className="action-prompt-text">📋 Add to action list?</span>}
      {state === "added" && <span className="action-prompt-text" style={{color:"#4caf50"}}>✓ Added</span>}
      {state === "idle" && (
        <button className="action-btn" onClick={handleAdd}>Yes, add it</button>
      )}
      {state === "adding" && (
        <button className="action-btn adding" onClick={()=>{}}>Yes, add it</button>
      )}
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("loading");
  const [loginTab, setLoginTab] = useState("user");
  const [loginId, setLoginId] = useState("");
  const [loginPin, setLoginPin] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCoach, setIsCoach] = useState(false);
  const [users, setUsers] = useState(INITIAL_USERS);
  const [activeTab, setActiveTab] = useState("coach");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);
  const [scenarioResponse, setScenarioResponse] = useState("");
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [scenarioFeedback, setScenarioFeedback] = useState("");
  const [toast, setToast] = useState(null);
  const [selectedDashUser, setSelectedDashUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState("");
  const [inviteCompany, setInviteCompany] = useState("");
  const [managingUser, setManagingUser] = useState(null);
  const [manageForm, setManageForm] = useState({});
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [googleConnected, setGoogleConnected] = useState(false);
  const [bookingStep, setBookingStep] = useState("date"); // date | slots | payment | confirm
  const [bookingDate, setBookingDate] = useState("");
  const [bookingSlots, setBookingSlots] = useState([]);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [bookingDuration, setBookingDuration] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingSessionId, setBookingSessionId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [coachSessions, setCoachSessions] = useState([]);
  const [availForm, setAvailForm] = useState({ persona: "holly", day: "1", start: "09:00", end: "17:00" });
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [autoSaving, setAutoSaving] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState("Holly");
  const [actionItems, setActionItems] = useState([]);
  const [bannerCollapsed, setBannerCollapsed] = useState(false);
  const [bibleStudy, setBibleStudy] = useState(null);
  const [bibleLoading, setBibleLoading] = useState(false);
  const [showBiblePanel, setShowBiblePanel] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", role: "", company: "", industry: "", org_size: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSetupEmail, setPasswordSetupEmail] = useState("");
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);

  // ── HANDLE AUTH REDIRECT (password reset / invite) ──
  useEffect(() => {
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        const newPassword = window.prompt("Enter your new password:");
        if (newPassword) {
          const { error } = await supabase.auth.updateUser({ password: newPassword });
          if (error) alert("Error updating password: " + error.message);
          else alert("Password updated successfully! Please log in.");
        }
      }
    });
  }, []);

  // ── SESSION PERSISTENCE ──
  useEffect(() => {
    // Check for invite/recovery token in URL hash FIRST
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.replace("#", ""));
    const accessToken = hashParams.get("access_token");
    const tokenType = hashParams.get("type");
    const searchParams = new URLSearchParams(window.location.search);
    const isReset = window.location.pathname === "/reset";
    const isInvite = searchParams.get("type") === "invite" || tokenType === "recovery" || isReset;
    if (searchParams.get("google") === "connected") {
      window._googleJustConnected = true;
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (accessToken && isInvite) {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: hashParams.get("refresh_token") }).then(() => {
        // Mark user as needing password setup
        supabase.auth.updateUser({ data: { needs_password: true } });
        setScreen("password-setup");
        setLoginPin("");
        setNewPassword("");
        setConfirmPassword("");
        window.history.replaceState({}, document.title, window.location.pathname);
      });
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { setScreen("login"); return; }
      if (session?.user?.user_metadata?.needs_password) {
        setScreen("password-setup");
        return;
      }
      if (session) {
        const uid = session.user.id;
        supabase.from("profiles").select("*").eq("id", uid).single().then(({ data: profile }) => {
          const userName = profile?.name || session.user.email;
          const userRole = profile?.role || "Executive";
          const coachPersona = profile?.coach_persona || null;
          const faithIntegration = profile?.faith_integration || false;
          setCurrentUser(uid);
          setUsers(prev => ({ ...prev, [uid]: { name: userName, role: userRole, company: profile?.company || "", industry: profile?.industry || "", org_size: profile?.org_size || "", coach_persona: coachPersona, faith_integration: faithIntegration, sessions: [], assessments: [], notes: [] } }));
          if (profile?.is_coach) {
            setIsCoach(true);
            if (window._googleJustConnected) {
              window._googleJustConnected = false;
              setGoogleConnected(true);
              showToast("Google Calendar connected!");
            }
            setScreen("coach");
            return;
          }
          if (!coachPersona) { setScreen("onboarding"); return; }
          const firstName = userName.split(" ")[0];
          const introMsg = `Welcome, ${firstName}. I'm your Vantage OpEx executive performance coach. This is a confidential space to think clearly, lead better, and grow faster.\n\nWhat's the leadership challenge on your mind today?`;
          setMessages([{ role: "assistant", content: introMsg }]);
          setScreen("app");
          loadConversations(uid).then(async convos => {
            if (convos.length > 0) {
              const last = convos[0];
              const msgs = (last.messages || []).filter(m => typeof m.content === "string" && m.content.trim()).sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
              const hasUserMessages = msgs.some(m => m.role === "user");
              if (hasUserMessages) {
                setConversationId(last.id);
                const historyMsgs = msgs.map(m => ({ role: m.role, content: m.content }));
                setConversationHistory(historyMsgs);
                // Generate personalized greeting based on previous session
                const recentMsgs = msgs.slice(-10).map(m => ({ role: m.role, content: m.content }));
                const lastMsgTime = msgs[msgs.length - 1]?.created_at ? new Date(msgs[msgs.length - 1].created_at) : null;
                const minutesSince = lastMsgTime ? (Date.now() - lastMsgTime.getTime()) / 60000 : 999;
                let contextPrompt;
                if (minutesSince < 3) {
                  contextPrompt = "You are an elite executive coach. The client just reloaded the page mid-conversation — treat it as if the conversation was briefly interrupted. Acknowledge it naturally in one short sentence (e.g. 'Looks like we got cut off —') and continue exactly where you left off. No pleasantries.";
                } else if (minutesSince <= 195) {
                  contextPrompt = "You are an elite executive coach. The client stepped away briefly and has returned. Warmly acknowledge they're back, briefly recap the last point or action item in one sentence, and invite them to continue. 2 sentences max.";
                } else {
                  contextPrompt = "You are an elite executive coach. The client is returning after some time away. Greet them warmly by first name, reference the most recent action item or theme from last session in one specific sentence, then ask: would you like to pick up where we left off, or is there something else on your mind? 3 sentences max.";
                }
                try {
                  const greeting = await callClaude(recentMsgs, contextPrompt);
                  if (greeting && greeting.trim()) {
                    setMessages([...historyMsgs, { role: "assistant", content: greeting }]);
                  } else {
                    setMessages(historyMsgs);
                  }
                } catch(e) {
                  console.error("Greeting error:", e);
                  setMessages(historyMsgs);
                }
              }
            }
          }).catch(console.error);
          loadAssessments(uid).then(rows => {
            if (rows.length > 0) {
              // Filter only proper scored assessments (have scores field)
              const scoredRows = rows.filter(r => r.data?.scores);
              if (scoredRows.length > 0) {
                setAssessment(scoredRows[0].data);
                setUsers(prev => ({ ...prev, [uid]: { ...prev[uid], assessments: scoredRows.map(r => r.data) } }));
              }
            }
          }).catch(console.error);
          loadSessionNotes(uid).then(rows => {
            if (rows.length > 0) {
              const sessions = rows.map(r => r.data);
              setUsers(prev => ({ ...prev, [uid]: { ...prev[uid], sessions } }));
            }
          }).catch(console.error);
          loadActionItems(uid).then(items => setActionItems(items)).catch(console.error);
        });
      }
    });
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (activeTab === "coach") messagesEndRef.current?.scrollIntoView({ behavior: "instant" }); }, [activeTab]);

  const showToast = (msg) => { setToast(String(msg)); setTimeout(() => setToast(null), 5000); };

  const updateUser = useCallback((uid, updater) => {
    setUsers(prev => ({ ...prev, [uid]: updater(prev[uid]) }));
  }, []);

  // ── LOGIN ──
  const handleCoachEmailLogin = async () => {
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword,
      });
      if (error) throw error;
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
      if (!profile?.is_coach) throw new Error("Not a coach account.");
      setIsCoach(true);
      setScreen("coach");
    } catch(e) {
      setLoginError(e.message);
    }
    setLoginLoading(false);
  };

  const handleLogin = async () => {
    setLoginError("");
    if (loginTab === "coach") {
      if (loginPin === COACH_PIN) { setIsCoach(true); setScreen("coach"); setLoginPin(""); setLoginId(""); }
      else setLoginError("Incorrect coach PIN.");
      return;
    }
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginId.trim(),
        password: loginPin
      });
      if (error) { setLoginError(error.message); return; }
      const uid = data.user.id;
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", uid).single();
      setCurrentUser(uid);
      const userName = profile?.name || data.user.email;
      const userRole = profile?.role || "Executive"; // eslint-disable-line no-unused-vars
      const userCompany = profile?.company || "";
      const userIndustry = profile?.industry || "";
      const userOrgSize = profile?.org_size || "";
      const coachPersona = profile?.coach_persona || null;
      const faithIntegration = profile?.faith_integration || false;
      setUsers(prev => ({ ...prev, [uid]: { ...prev[uid], company: userCompany, industry: userIndustry, org_size: userOrgSize, coach_persona: coachPersona, faith_integration: faithIntegration } }));
      if (!coachPersona) { setScreen("onboarding"); return; }
      const firstName = userName.split(" ")[0];
      const introMsg = `Welcome, ${firstName}. I'm your Vantage OpEx executive performance coach. This is a confidential space to think clearly, lead better, and grow faster.\n\nWhat's the leadership challenge on your mind today?`;
      setMessages([{ role: "assistant", content: introMsg }]);
      setScreen("app");
      setLoginPin(""); setLoginId("");
      loadConversations(uid).then(async convos => {
        if (convos.length > 0) {
          const last = convos[0];
          const msgs = (last.messages || []).filter(m => typeof m.content === "string" && m.content.trim()).sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
          const hasUserMessages = msgs.some(m => m.role === "user");
          if (hasUserMessages) {
            setConversationId(last.id);
            const historyMsgs = msgs.map(m => ({ role: m.role, content: m.content }));
            setConversationHistory(historyMsgs);
            const recentMsgs = msgs.slice(-10).map(m => ({ role: m.role, content: m.content }));
            const contextPrompt = "You are an elite executive coach. The client is starting a new session. Review the conversation history and craft a brief warm highly specific opening. Lead immediately with a direct follow-up on a specific action item or commitment they made. Recap the core theme from last session in one line. Close with: What is on your mind today? Be direct, warm, specific. 3 sentences max.";
            try {
              const greeting = await callClaude(recentMsgs, contextPrompt);
              setMessages([...historyMsgs, { role: "assistant", content: greeting }]);
            } catch(e) {
              console.error("Greeting error:", e);
              setMessages(historyMsgs);
            }
          }
        }
      }).catch(console.error);
      loadAssessments(uid).then(rows => {
        if (rows.length > 0) {
          const scoredRows = rows.filter(r => r.data?.scores);
          if (scoredRows.length > 0) {
            setAssessment(scoredRows[0].data);
            setUsers(prev => ({ ...prev, [uid]: { ...prev[uid], assessments: scoredRows.map(r => r.data) } }));
          }
        }
      }).catch(console.error);
      loadSessionNotes(uid).then(rows => {
        if (rows.length > 0) {
          const sessions = rows.map(r => r.data);
          setUsers(prev => ({ ...prev, [uid]: { ...prev[uid], sessions } }));
        }
      }).catch(console.error);
      loadActionItems(uid).then(items => setActionItems(items)).catch(console.error);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  // ── LOAD INVITED USERS ──
  useEffect(() => {
    if (screen === "coach") {
      loadInvitedUsers().then(data => {
        setInvitedUsers(data);
        // Add accepted users to users list
        data.filter(u => u.accepted).forEach(u => {
          setUsers(prev => ({
            ...prev,
            [u.email]: { name: u.name, role: u.role, company: u.company || "", sessions: [], assessments: [] }
          }));
        });
      }).catch(console.error);
    }
  }, [screen]);

  // ── VOICE ──
  const speak = useCallback((text) => {
    if (!voiceEnabled) return;
    synthRef.current.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.92; u.pitch = 0.95;
    const voices = synthRef.current.getVoices();
    const v = voices.find(v => v.name.includes("Daniel") || v.name.includes("Alex") || v.name.includes("Google UK"));
    if (v) u.voice = v;
    synthRef.current.speak(u);
  }, [voiceEnabled]);

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast("Voice not supported"); return; }
    const rec = new SR();
    rec.continuous = false; rec.interimResults = false; rec.lang = "en-US";
    rec.onresult = (e) => { setInput(e.results[0][0].transcript); setIsListening(false); };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
    rec.start(); setIsListening(true);
  };

  // ── PASSWORD RESET ──
  const handleForgotPassword = async () => {
    if (!loginId.trim()) { setLoginError("Please enter your email first."); return; }
    const { error } = await supabase.auth.resetPasswordForEmail(loginId.trim(), {
      redirectTo: 'https://app.vantageopex.com/reset'
    });
    if (error) { setLoginError(error.message); return; }
    setResetSent(true);
  };

  // ── HANDLE INVITE ──
  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteFirstName.trim()) { setInviteError("Email and first name are required."); return; }
    const inviteName = `${inviteFirstName.trim()} ${inviteLastName.trim()}`.trim();
    setInviteLoading(true);
    setInviteError("");
    try {
      await inviteUser(inviteEmail.trim(), inviteName.trim(), inviteRole.trim() || "Executive", inviteCompany.trim());
      setInvitedUsers(prev => {
        const exists = prev.find(u => u.email === inviteEmail);
        if (exists) return prev.map(u => u.email === inviteEmail ? { ...u, name: inviteName, role: inviteRole || "Executive" } : u);
        return [{ email: inviteEmail, name: inviteName, role: inviteRole || "Executive", accepted: false, created_at: new Date().toISOString() }, ...prev];
      });
      setInviteEmail(""); setInviteFirstName(""); setInviteLastName(""); setInviteRole(""); setInviteCompany("");
      showToast("Invite sent to " + inviteEmail);
    } catch (err) {
      if (err.message.includes("already been registered")) {
        setInviteError("This email already has an account.");
      } else {
        setInviteError(err.message);
      }
    }
    setInviteLoading(false);
  };

  const handleCancelInvite = async (email) => {
    try {
      await supabase.from("invited_users").delete().eq("email", email);
      setInvitedUsers(prev => prev.filter(u => u.email !== email));
      showToast("Invite cancelled for " + email);
    } catch (err) {
      showToast("Error: " + err.message);
    }
  };

  // ── AUTO-SAVE ──
  const autoSaveNotes = useCallback(async (msgs, uid) => {
    if (!uid || msgs.filter(m => m.role === "user").length < 2) return;
    setAutoSaving(true);
    try {
      // Only pass string content to avoid complex content errors
      const apiMsgs = [
        ...msgs
          .filter(m => typeof m.content === "string" && m.content.trim())
          .slice(-20)
          .map(m => ({ role: m.role, content: m.content })),
        { role: "user", content: NOTES_PROMPT }
      ];
      const res = await callClaude(apiMsgs, "Return ONLY valid JSON, no markdown.");
      const parsed = JSON.parse(res.replace(/```json|```/g, "").trim());
      parsed.date = new Date().toLocaleDateString();
      parsed.msgCount = msgs.filter(m => m.role === "user").length;
      parsed.autoSaved = true;
      // Save to Supabase
      await saveSessionNotes(uid, parsed);
      setUsers(prev => {
        const u = prev[uid];
        if (!u) return prev;
        const sessions = [...(u.sessions || [])];
        const lastIdx = sessions.length - 1;
        if (lastIdx >= 0 && sessions[lastIdx].autoSaved) sessions[lastIdx] = parsed;
        else sessions.push(parsed);
        return { ...prev, [uid]: { ...u, sessions } };
      });
    } catch(e) { console.error("Notes error:", e.message, e); }
    setAutoSaving(false);
  }, []);

  // ── SEND MESSAGE ──
  const runBibleStudy = async (depth = "quick") => {
    setBibleLoading(true);
    try {
      const recentMsgs = messages.slice(-6).filter(m => typeof m.content === "string");
      const topicRes = await callClaude(recentMsgs, "In 3-5 keywords, what is the core leadership or life challenge being discussed? Return only the keywords, nothing else.");
      const topic = topicRes.trim();
      const verses = await searchBible(topic, depth === "quick" ? 3 : 7);
      const verseText = verses.map(v => v.book + " " + v.chapter + ":" + v.verse + " - \"" + v.text + "\"").join("\n");
      const studyPrompt = depth === "quick"
        ? "The client faces a challenge around: \"" + topic + "\". Verses:\n\n" + verseText + "\n\nProvide a brief 3-4 sentence reflection connecting these to their leadership challenge. End with one reflection question."
        : "The client faces a challenge around: \"" + topic + "\". Verses:\n\n" + verseText + "\n\nProvide a deeper 2-3 paragraph study with historical context, practical application, and a closing prayer or reflection prompt.";
      const insight = await callClaude([{ role: "user", content: studyPrompt }], "You are a wise biblical coach who connects scripture to real business and leadership challenges. Be warm, practical, and spiritually grounded.");
      setBibleStudy({ topic, verses, insight, depth });
    } catch(e) { console.error("Bible study error:", e); }
    setBibleLoading(false);
  };

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) return;
    setProfileSaving(true);
    try {
      // Fallback to session user if currentUser is null (e.g. coach login)
      let uid = currentUser;
      if (!uid) {
        const { data: { session } } = await supabase.auth.getSession();
        uid = session?.user?.id;
        if (uid) setCurrentUser(uid);
      }
      if (!uid) { showToast("Session expired — please log in again."); setProfileSaving(false); return; }
      const { error: profileError } = await supabase.from("profiles").update({
        name: profileForm.name.trim(),
        role: profileForm.role.trim(),
        company: profileForm.company.trim(),
        industry: profileForm.industry.trim(),
        org_size: profileForm.org_size.trim(),
        coach_persona: profileForm.coach_persona || "Holly",
        faith_integration: profileForm.faith_integration || false
      }).eq("id", uid);
      if (profileError) { showToast("Save failed: " + profileError.message); return; }
      setUsers(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), name: profileForm.name.trim(), role: profileForm.role.trim(), company: profileForm.company.trim(), industry: profileForm.industry.trim(), org_size: profileForm.org_size.trim(), coach_persona: profileForm.coach_persona || "Holly", faith_integration: profileForm.faith_integration || false } }));
      setShowProfileModal(false);
      showToast("Profile updated!");
    } catch(e) { showToast("Error: " + e.message); }
    setProfileSaving(false);
  };

  const sendMessage = async () => {
    if (!input.trim() && attachments.length === 0) return;
    if (loading) return;
    // Build display message (text only for chat history)
    const attachmentText = attachments.length > 0 ? `

[Attached: ${attachments.map(a => a.name).join(", ")}]` : "";
    const userMsg = { role: "user", content: input.trim() + attachmentText };
    // Build API message with actual file content
    const apiContent = [];
    if (input.trim()) apiContent.push({ type: "text", text: input.trim() });
    attachments.forEach(a => {
      if (a.type.startsWith("image/")) {
        apiContent.push({ type: "image", source: { type: "base64", media_type: a.type, data: a.data.split(",")[1] } });
      } else {
        apiContent.push({ type: "text", text: `[Attached file: ${a.name}. File content not extractable in browser.]` });
      }
    });
    setAttachments([]);
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);
    try {
      // Build API messages from full message history for context
      const allMsgs = newMsgs
        .filter(m => typeof m.content === "string" && m.content.trim())
        .slice(-30)
        .map((m, i, arr) => {
          if (i === arr.length - 1 && apiContent.length > 0) return { role: m.role, content: apiContent };
          return { role: m.role, content: m.content };
        });
      const reply = await callClaude(allMsgs, buildCoachSystem(user));
      const finalMsgs = [...newMsgs, { role: "assistant", content: reply }];
      setMessages(finalMsgs);
      if (voiceEnabled) speak(reply);
      // Auto-save to Supabase after every exchange
      // Check if user confirmed adding an action item
      const lastUserMsg = input.trim().toLowerCase();
      const prevAssistantMsg = messages[messages.length - 1]?.content?.toLowerCase() || "";
      if ((lastUserMsg === "yes" || lastUserMsg === "yeah" || lastUserMsg === "sure" || lastUserMsg === "please" || lastUserMsg === "add it") 
          && prevAssistantMsg.includes("add that to your action list")) {
        // Extract the action item from conversation context
        extractAndSaveActionItems(currentUser, conversationId, finalMsgs).then(items => {
          if (items) loadActionItems(currentUser).then(all => setActionItems(all));
        });
      }

      saveConversation(currentUser, finalMsgs, conversationId).then(id => {
        const convId = conversationId || id;
        if (!conversationId) setConversationId(id);
        // Extract action items every 2 exchanges
        const userCount = finalMsgs.filter(m => m.role === "user").length;
        if (userCount % 2 === 0) {
          extractAndSaveActionItems(currentUser, convId, finalMsgs).then(items => {
            if (items) loadActionItems(currentUser).then(all => setActionItems(all));
          });
        }
      }).catch(err => console.error("Save failed:", err));
      const userCount = finalMsgs.filter(m => m.role === "user").length;
      if (userCount >= 3 && userCount % 3 === 0) setTimeout(() => autoSaveNotes(finalMsgs, currentUser), 2000);
    } catch (err) { showToast("Error: " + err.message); console.error("sendMessage error:", err); }
    setLoading(false);
  };

  // ── ASSESSMENT ──
  const runAssessment = async () => {
    if (messages.length < 4) { showToast("Have a coaching conversation first"); return; }
    setAssessmentLoading(true);
    setActiveTab("assessment");
    try {
      const apiMsgs = [...messages.map(m => ({ role: m.role, content: m.content })), { role: "user", content: ASSESSMENT_PROMPT }];
      const res = await callClaude(apiMsgs, "Return ONLY valid JSON, no markdown.");
      const parsed = JSON.parse(res.replace(/```json|```/g, "").trim());
      parsed.date = new Date().toLocaleDateString();
      setAssessment(parsed);
      updateUser(currentUser, u => ({ ...u, assessments: [...u.assessments, parsed] }));
      saveAssessment(currentUser, parsed).catch(console.error);
    } catch (err) { showToast("Assessment error: " + err.message); }
    setAssessmentLoading(false);
  };

  // ── SCENARIO ──
  const submitScenario = async () => {
    if (!scenarioResponse.trim() || scenarioLoading) return;
    setScenarioLoading(true);
    try {
      const prompt = `Scenario: "${activeScenario.description}"\nExecutive's response: "${scenarioResponse}"\nProvide sharp coaching feedback. Rate across: Strategic Thinking, Emotional Intelligence, Decisiveness, Communication. End with one powerful question.`;
      const feedback = await callClaude([{ role: "user", content: prompt }], buildCoachSystem(user));
      setScenarioFeedback(feedback);
    } catch (err) { showToast("Error: " + err.message); }
    setScenarioLoading(false);
  };

  const user = currentUser ? users[currentUser] : null;
  const lastSession = user?.sessions?.length > 0 ? user.sessions[user.sessions.length - 1] : null;
  const prevActionItems = lastSession?.autoSaved === false ? [] : (user?.sessions?.slice(-2, -1)[0]?.actionItems || []);

  // ── LOADING SCREEN ──
  if (screen === "loading") return (
    <>
      <style>{CSS}</style>
      <div className="login-wrap">
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
          <LogoMark size={52} />
          <div className="spinner" />
        </div>
      </div>
    </>
  );

  // ── PASSWORD SETUP SCREEN (new invite users) ──
  const handleSetPassword = async () => {
    setPasswordError("");
    if (newPassword.length < 6) { setPasswordError("Password must be at least 6 characters."); return; }
    if (newPassword !== confirmPassword) { setPasswordError("Passwords do not match."); return; }
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword, data: { needs_password: false } });
      if (error) throw error;
      await supabase.auth.signOut();
      setScreen("login");
      setNewPassword("");
      setConfirmPassword("");
      setLoginPin("");
      showToast("Password created! Please log in.");
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  if (loginTab === "user" && loginPin === "1010") setLoginPin("");

  if (screen === "password-setup") return (
    <>
      <style>{CSS}</style>
      <div className="login-wrap">
        <div className="login-card">
          <div style={{textAlign:"center",marginBottom:32}}>
            <LogoMark size={48} />
            <div style={{marginTop:16,fontSize:13,letterSpacing:3,color:"#26a5cc",fontFamily:"Montserrat,sans-serif",textTransform:"uppercase"}}>Vantage OpEx</div>
          </div>
          <h2 style={{fontSize:20,fontWeight:400,color:"#fff",marginBottom:8,fontFamily:"Montserrat,sans-serif"}}>{window.location.pathname === "/reset" ? "Reset Your Password" : "Set Your Password"}</h2>
          <p style={{fontSize:13,color:"#888",marginBottom:28,lineHeight:1.6,fontFamily:"Montserrat,sans-serif"}}>{window.location.pathname === "/reset" ? "Enter your new password below." : "Welcome! Create a password to access your coaching dashboard. Minimum 6 characters."}</p>
          <div style={{position:"relative",marginBottom:16}}>
            <input
              type={showNewPassword ? "text" : "password"}
              placeholder="New password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={{width:"100%",padding:"12px 44px 12px 16px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"#fff",fontSize:15,boxSizing:"border-box",fontFamily:"Montserrat,sans-serif"}}
            />
            <span onClick={() => setShowNewPassword(p => !p)} style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:18,color:"#888"}}>
              {showNewPassword ? "🙈" : "👁️"}
            </span>
          </div>
          <div style={{position:"relative",marginBottom:24}}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSetPassword()}
              style={{width:"100%",padding:"12px 44px 12px 16px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"#fff",fontSize:15,boxSizing:"border-box",fontFamily:"Montserrat,sans-serif"}}
            />
            <span onClick={() => setShowConfirmPassword(p => !p)} style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:18,color:"#888"}}>
              {showConfirmPassword ? "🙈" : "👁️"}
            </span>
          </div>
          {passwordError && <div style={{color:"#e57373",fontSize:13,marginBottom:16,fontFamily:"Montserrat,sans-serif"}}>{passwordError}</div>}
          <button onClick={handleSetPassword} style={{width:"100%",padding:"13px",background:"#26a5cc",border:"none",borderRadius:8,color:"#fff",fontSize:15,cursor:"pointer",fontFamily:"Montserrat,sans-serif",letterSpacing:1}}>
            Set Password & Continue
          </button>
        </div>
      </div>
    </>
  );

  // ── ONBOARDING SCREEN ──
  if (screen === "onboarding") return (
    <>
      <style>{CSS}</style>
      <div className="login-wrap" style={{background:`linear-gradient(135deg,${BRAND.navy} 0%,${BRAND.navyLight} 60%,#1a3550 100%)`}}>
        <div style={{width:"100%",maxWidth:520,margin:"0 auto",padding:"0 24px"}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <LogoMark size={52} />
            <div style={{marginTop:16,fontSize:11,letterSpacing:3,color:"#26a5cc",fontFamily:"Montserrat,sans-serif",textTransform:"uppercase"}}>Vantage OpEx</div>
            <h1 style={{fontSize:24,fontWeight:300,color:"#fff",marginTop:16,fontFamily:"Montserrat,sans-serif",letterSpacing:1}}>Welcome to your coaching space</h1>
            <p style={{fontSize:13,color:"#888",marginTop:8,lineHeight:1.8,fontFamily:"Montserrat,sans-serif"}}>Before we begin, let's personalize your experience.</p>
          </div>

          <div style={{marginBottom:32}}>
            <div style={{fontSize:11,color:"#72bfd0",marginBottom:16,fontFamily:"Montserrat,sans-serif",letterSpacing:2,textTransform:"uppercase",textAlign:"center"}}>Choose your coach</div>
            <div style={{display:"flex",gap:16}}>
              {["Holly","David"].map(persona => (
                <div key={persona} onClick={() => setSelectedPersona(persona)} style={{flex:1,padding:"24px 16px",borderRadius:14,border:`2px solid ${selectedPersona === persona ? "#26a5cc" : "rgba(255,255,255,0.08)"}`,background: selectedPersona === persona ? "rgba(38,165,204,0.08)" : "rgba(255,255,255,0.02)",cursor:"pointer",textAlign:"center",transition:"all 0.2s"}}>
                  <div style={{fontSize:36,marginBottom:10}}>{persona === "Holly" ? "👩" : "👨"}</div>
                  <div style={{fontSize:16,fontWeight:600,color:"#fff",fontFamily:"Montserrat,sans-serif",marginBottom:6}}>{persona}</div>
                  <div style={{fontSize:11,color:"#888",fontFamily:"Montserrat,sans-serif",lineHeight:1.5}}>{persona === "Holly" ? "Warm, direct & intuitive" : "Strategic, grounded & precise"}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{marginBottom:32}}>
            <div style={{fontSize:11,color:"#72bfd0",marginBottom:16,fontFamily:"Montserrat,sans-serif",letterSpacing:2,textTransform:"uppercase",textAlign:"center"}}>Faith Integration</div>
            <div onClick={async () => {
              const newVal = !(user?.faith_integration);
              await supabase.from("profiles").update({ faith_integration: newVal }).eq("id", currentUser);
              setUsers(prev => ({ ...prev, [currentUser]: { ...prev[currentUser], faith_integration: newVal } }));
            }} style={{display:"flex",alignItems:"center",gap:16,padding:"18px 20px",borderRadius:12,border:`1px solid ${user?.faith_integration ? "rgba(38,165,204,0.3)" : "rgba(255,255,255,0.08)"}`,background: user?.faith_integration ? "rgba(38,165,204,0.06)" : "rgba(255,255,255,0.02)",cursor:"pointer",transition:"all 0.2s"}}>
              <div style={{width:24,height:24,borderRadius:"50%",border:`2px solid ${user?.faith_integration ? "#26a5cc" : "rgba(255,255,255,0.2)"}`,background: user?.faith_integration ? "#26a5cc" : "transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.2s"}}>
                {user?.faith_integration && <span style={{color:"#fff",fontSize:13,fontWeight:"bold"}}>✓</span>}
              </div>
              <div>
                <div style={{fontSize:14,color:"#e8e8e8",fontFamily:"Montserrat,sans-serif",fontWeight:500}}>Include biblical wisdom</div>
                <div style={{fontSize:12,color:"#888",fontFamily:"Montserrat,sans-serif",marginTop:3,lineHeight:1.5}}>Your coach will offer scripture-based insights tied to your leadership challenges when relevant</div>
              </div>
            </div>
          </div>

          <button onClick={async () => {
            await supabase.from("profiles").update({ coach_persona: selectedPersona }).eq("id", currentUser);
            setUsers(prev => ({ ...prev, [currentUser]: { ...prev[currentUser], coach_persona: selectedPersona } }));
            setScreen("app");
          }} style={{width:"100%",padding:"16px",background:"#26a5cc",border:"none",borderRadius:10,color:"#fff",fontSize:15,cursor:"pointer",fontFamily:"Montserrat,sans-serif",fontWeight:600,letterSpacing:1}}>
            Begin with {selectedPersona} →
          </button>
        </div>
      </div>
    </>
  );

  // ── LOGIN SCREEN ──
  if (screen === "login") return (
    <>
      <style>{CSS}</style>
      <div className="login-wrap">
        <div className="login-card">
          <svg className="login-card-bg" viewBox="0 0 420 620" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
            {/* Diagonal lines — dense */}
            <line x1="0" y1="80" x2="420" y2="0" stroke="#8dd9ed" strokeWidth="0.6"/>
            <line x1="0" y1="160" x2="420" y2="60" stroke="#8dd9ed" strokeWidth="0.5"/>
            <line x1="0" y1="260" x2="420" y2="140" stroke="#8dd9ed" strokeWidth="0.6"/>
            <line x1="0" y1="360" x2="420" y2="220" stroke="#8dd9ed" strokeWidth="0.4"/>
            <line x1="0" y1="460" x2="420" y2="320" stroke="#8dd9ed" strokeWidth="0.5"/>
            <line x1="0" y1="560" x2="420" y2="420" stroke="#8dd9ed" strokeWidth="0.4"/>
            <line x1="60" y1="0" x2="420" y2="480" stroke="#8dd9ed" strokeWidth="0.5"/>
            <line x1="180" y1="0" x2="420" y2="340" stroke="#8dd9ed" strokeWidth="0.4"/>
            <line x1="300" y1="0" x2="420" y2="160" stroke="#8dd9ed" strokeWidth="0.6"/>
            <line x1="0" y1="200" x2="280" y2="620" stroke="#8dd9ed" strokeWidth="0.4"/>
            <line x1="140" y1="620" x2="420" y2="100" stroke="#8dd9ed" strokeWidth="0.5"/>
            {/* Overlapping polygons */}
            <polygon points="300,20 400,100 360,200 240,160 200,60" fill="none" stroke="#8dd9ed" strokeWidth="0.7"/>
            <polygon points="340,60 420,160 380,260 260,220 220,100" fill="none" stroke="#8dd9ed" strokeWidth="0.5"/>
            <polygon points="0,300 100,240 180,320 140,420 20,400" fill="none" stroke="#8dd9ed" strokeWidth="0.6"/>
            <polygon points="40,340 140,280 200,360 160,460 40,440" fill="none" stroke="#8dd9ed" strokeWidth="0.4"/>
            <polygon points="220,440 340,400 380,500 300,560 180,520" fill="none" stroke="#8dd9ed" strokeWidth="0.6"/>
            <polygon points="260,480 360,440 400,520 320,580 200,560" fill="none" stroke="#8dd9ed" strokeWidth="0.4"/>
            <polygon points="100,80 200,40 260,120 200,200 80,180" fill="none" stroke="#8dd9ed" strokeWidth="0.5"/>
            {/* Half circles — top, bottom, sides */}
            <path d="M 210 0 A 90 90 0 0 1 390 0" fill="none" stroke="#8dd9ed" strokeWidth="0.6"/>
            <path d="M 30 0 A 70 70 0 0 1 170 0" fill="none" stroke="#8dd9ed" strokeWidth="0.5"/>
            <path d="M 0 480 A 80 80 0 0 0 0 320" fill="none" stroke="#8dd9ed" strokeWidth="0.6"/>
            <path d="M 420 200 A 90 90 0 0 1 420 380" fill="none" stroke="#8dd9ed" strokeWidth="0.5"/>
            <path d="M 100 620 A 100 100 0 0 0 300 620" fill="none" stroke="#8dd9ed" strokeWidth="0.6"/>
            <path d="M 280 620 A 70 70 0 0 0 420 620" fill="none" stroke="#8dd9ed" strokeWidth="0.4"/>
            {/* Cross lines */}
            <line x1="0" y1="0" x2="420" y2="620" stroke="#8dd9ed" strokeWidth="0.3"/>
            <line x1="420" y1="0" x2="0" y2="620" stroke="#8dd9ed" strokeWidth="0.3"/>
          </svg>
          <div className="login-logo"><LogoMark size={52} /></div>
          <div className="login-brand">VANTAGE <span>OpEx</span></div>
          <div className="login-sub">Executive Performance Platform</div>
          <div className="login-tabs">
            <button className={`login-tab ${loginTab === "user" ? "active" : ""}`} onClick={() => { setLoginTab("user"); setLoginError(""); }}>Client Login</button>
            <button className={`login-tab ${loginTab === "coach" ? "active" : ""}`} onClick={() => { setLoginTab("coach"); setLoginError(""); }}>Coach Access</button>
          </div>
          {loginTab === "user" && (
            <>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" placeholder="Enter your email" type="email" value={loginId} onChange={e => setLoginId(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{position:"relative"}}>
                  <input className="form-input" type={showNewPassword ? "text" : "password"} placeholder="Enter your password" value={loginPin} onChange={e => setLoginPin(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} style={{paddingRight:44,width:"100%",boxSizing:"border-box"}} />
                  <span onClick={() => setShowNewPassword(p => !p)} style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:18,color:"#888"}}>
                    {showNewPassword ? "🙈" : "👁️"}
                  </span>
                </div>
              </div>
            </>
          )}
          {loginTab === "coach" && (
            <div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" placeholder="coach@example.com" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleCoachEmailLogin()} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" placeholder="Password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleCoachEmailLogin()} />
              </div>
            </div>
          )}
          {loginError && <div className="login-error">{loginError}</div>}
          {resetSent && <div style={{color:"#4caf50",fontSize:13,marginTop:8}}>Password reset email sent! Check your inbox.</div>}
          {loginTab === "user" && !resetSent && (
            <div style={{textAlign:"right",marginTop:4}}>
              <span style={{fontSize:12,color:"#72bfd0",cursor:"pointer",textDecoration:"underline",fontFamily:"Montserrat,sans-serif",letterSpacing:"0.5px"}} onClick={handleForgotPassword}>Forgot password?</span>
            </div>
          )}
          <button className="btn-login" onClick={loginTab === "coach" ? handleCoachEmailLogin : handleLogin}>
            {loginTab === "coach" ? "Enter Coach Dashboard →" : "Begin Session →"}
          </button>
        </div>
      </div>
    </>
  );

  // ── COACH DASHBOARD ──
  if (screen === "coach") {
    const dashUser = selectedDashUser ? users[selectedDashUser] : null;
    const latestAssessment = dashUser?.assessments[dashUser.assessments.length - 1];
    const prevAssessment = dashUser?.assessments[dashUser.assessments.length - 2];
    return (
      <>
        <style>{CSS}</style>
        <div className="app">
          <header className="header">
            <div className="logo">
              <LogoMark size={32} />
              <div>
                <div className="logo-text">VANTAGE <span>OpEx</span></div>
                <div className="logo-sub">Coach Dashboard</div>
              </div>
            </div>
            <div className="header-right">
              <button className="btn-sm" onClick={() => setScreen("app")} style={{marginRight:8}}>Go to Chat</button>
              <button className="btn-sm" onClick={() => { setScreen("login"); setSelectedDashUser(null); }}>← Sign Out</button>
            </div>
          </header>
          <main className="content">
            <div className="dash-grid">
              <div style={{display:"flex",flexDirection:"column",gap:18}}>
              <div className="card">
                <div className="card-title">Invite Client</div>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="form-input" placeholder="First name" value={inviteFirstName} onChange={e => setInviteFirstName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="form-input" placeholder="Last name" value={inviteLastName} onChange={e => setInviteLastName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <input className="form-input" placeholder="e.g. CFO, COO" value={inviteRole} onChange={e => setInviteRole(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Company</label>
                  <input className="form-input" placeholder="Company name" value={inviteCompany} onChange={e => setInviteCompany(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" placeholder="client@company.com" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
                </div>
                {inviteError && <div className="login-error">{inviteError}</div>}
                <button className="btn-primary" style={{width:"100%"}} disabled={inviteLoading || !inviteEmail || !inviteFirstName} onClick={handleInvite}>{inviteLoading ? "Sending..." : "Send Invite →"}</button>
                <div style={{marginTop:24}}>
                  <div className="card-title">Invited Clients</div>
                  <div className="user-list">
                    {invitedUsers.filter(u => !u.accepted).map((u, i) => (
                      <div key={i} className="user-row" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div className="user-name">{u.name}</div>
                          <div className="user-role">{u.role} · {u.email}</div>
                          <div className="user-stat" style={{marginTop:6}}>{u.accepted ? <span style={{color:"#4caf50"}}>✓ Accepted</span> : <span style={{color:"#b8d4de"}}>⏳ Pending</span>}</div>
                        </div>
                        {!u.accepted && (
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            <button className="btn-sm" style={{color:"#26a5cc",borderColor:"rgba(38,165,204,0.3)"}} onClick={async () => {
                              try {
                                const res = await fetch("/api/invite", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: u.email, name: u.name, role: u.role, company: u.company }) });
                                if (res.ok) showToast("Invite resent to " + u.email);
                                else showToast("Failed to resend invite");
                              } catch(e) { showToast("Error: " + e.message); }
                            }}>Resend</button>
                            <button className="btn-sm" style={{color:"#e57373",borderColor:"rgba(229,115,115,0.3)"}} onClick={() => handleCancelInvite(u.email)}>Cancel</button>
                          </div>
                        )}
                      </div>
                    ))}
                    {invitedUsers.length === 0 && <div style={{color:"#3a5a6e",fontSize:13}}>No clients invited yet.</div>}
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-title">Clients</div>
                <div className="user-list">
                  {Object.entries(users).map(([uid, u]) => (
                    <div key={uid} className={`user-row ${selectedDashUser === uid ? "active" : ""}`}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div onClick={() => setSelectedDashUser(uid)} style={{flex:1,cursor:"pointer"}}>
                          <div className="user-name">{u.name}</div>
                          <div className="user-role">{u.role}{u.company ? ` · ${u.company}` : ""}</div>
                          <div className="user-stats">
                            <div className="user-stat">Sessions: <span>{u.sessions.length}</span></div>
                            <div className="user-stat">Assessments: <span>{u.assessments.length}</span></div>
                          </div>
                        </div>
                        <button className="btn-sm" style={{marginTop:4,flexShrink:0}} onClick={() => { setManagingUser(uid); const parts = (u.name || "").split(" "); setManageForm({ firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || "", role: u.role || "", company: u.company || "" }); }}>Manage</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-title">Google Calendar</div>
                {googleConnected ? (
                  <div style={{color:"#4caf50",fontSize:14,marginBottom:16}}>✓ Connected</div>
                ) : (
                  <button className="btn-primary" style={{width:"100%",marginBottom:16}} onClick={async () => {
                    const { data: { session } } = await supabase.auth.getSession();
                    const token = session?.access_token || "";
                    console.log("OAuth session:", session ? "exists" : "null", "token length:", token.length);
                    window.location.href = `/api/calendar?action=auth&token=${token}`;
                  }}>Connect Google Calendar →</button>
                )}
                <div className="card-title" style={{marginTop:8}}>Set Availability</div>
                <div className="form-group">
                  <label className="form-label">Persona</label>
                  <select className="form-input" value={availForm.persona} onChange={e => setAvailForm(p => ({...p, persona: e.target.value}))}>
                    <option value="holly">Holly</option>
                    <option value="david">David</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Day</label>
                  <select className="form-input" value={availForm.day} onChange={e => setAvailForm(p => ({...p, day: e.target.value}))}>
                    <option value="0">Sunday</option>
                    <option value="1">Monday</option>
                    <option value="2">Tuesday</option>
                    <option value="3">Wednesday</option>
                    <option value="4">Thursday</option>
                    <option value="5">Friday</option>
                    <option value="6">Saturday</option>
                  </select>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <div className="form-group" style={{flex:1}}>
                    <label className="form-label">From</label>
                    <input className="form-input" type="time" value={availForm.start} onChange={e => setAvailForm(p => ({...p, start: e.target.value}))} />
                  </div>
                  <div className="form-group" style={{flex:1}}>
                    <label className="form-label">To</label>
                    <input className="form-input" type="time" value={availForm.end} onChange={e => setAvailForm(p => ({...p, end: e.target.value}))} />
                  </div>
                </div>
                <button className="btn-primary" style={{width:"100%"}} onClick={async () => {
                  const { error } = await supabase.from("availability").insert({
                    coach_persona: availForm.persona,
                    day_of_week: parseInt(availForm.day),
                    start_time: availForm.start,
                    end_time: availForm.end,
                  });
                  if (!error) {
                    const { data } = await supabase.from("availability").select("*").order("day_of_week");
                    setAvailability(data || []);
                    showToast("Availability saved!");
                  }
                }}>Add Window →</button>
                {availability.length > 0 && (
                  <div style={{marginTop:16}}>
                    {availability.map((a, i) => (
                      <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.06)",fontSize:13}}>
                        <span style={{color:"#b8d4de"}}>{["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][a.day_of_week]} · {a.coach_persona}</span>
                        <span style={{color:"#3a8fa3"}}>{a.start_time.slice(0,5)} – {a.end_time.slice(0,5)}</span>
                        <button className="btn-sm" style={{color:"#e57373",borderColor:"rgba(229,115,115,0.3)",padding:"2px 8px",fontSize:11}} onClick={async () => {
                          await supabase.from("availability").delete().eq("id", a.id);
                          setAvailability(prev => prev.filter(x => x.id !== a.id));
                        }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="card-title" style={{marginTop:24}}>Upcoming Sessions</div>
                {coachSessions.filter(s => s.status === "confirmed" && new Date(s.start_time) > new Date()).length === 0 ? (
                  <div style={{color:"#3a5a6e",fontSize:13}}>No upcoming sessions.</div>
                ) : (
                  coachSessions.filter(s => s.status === "confirmed" && new Date(s.start_time) > new Date()).map((s, i) => (
                    <div key={i} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                      <div style={{color:"#e0eef5",fontSize:14,fontWeight:500}}>{s.profiles?.name}</div>
                      <div style={{color:"#3a8fa3",fontSize:13}}>{new Date(s.start_time).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})} · {new Date(s.start_time).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})} · {s.duration_minutes}min</div>
                      <div style={{color:"#b8d4de",fontSize:12,marginTop:2}}>{s.coach_persona === "holly" ? "Holly" : "David"} · ${s.amount_cents / 100}</div>
                    </div>
                  ))
                )}
              </div>
              </div>
              <div className="dash-detail">
                {!dashUser && <div className="card"><div className="no-user">← Select a client to view details</div></div>}
                {dashUser && (
                  <>
                    <div className="card">
                      <div className="card-title">{dashUser.name} — {dashUser.role}</div>
                      {latestAssessment ? (
                        <>
                          <div className="detail-section">Competency Scores — {latestAssessment.date}</div>
                          <div className="progress-chart">
                            {SCORE_KEYS.map(k => {
                              const val = latestAssessment.scores[k] || 0;
                              const prev = prevAssessment?.scores[k];
                              const diff = prev != null ? val - prev : null;
                              return (
                                <div key={k} className="score-row">
                                  <div className="score-label">{k}</div>
                                  <div className="score-bar-wrap"><div className="score-bar" style={{ width: `${val * 10}%` }} /></div>
                                  <div className="score-val">{val}</div>
                                  {diff != null && <div className={diff > 0 ? "trend-up" : diff < 0 ? "trend-down" : "score-val"}>{diff > 0 ? `+${diff}` : diff < 0 ? diff : "—"}</div>}
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ marginTop: 18 }}>
                            <div className="detail-section">Latest Insight</div>
                            <div className="insight-text">{latestAssessment.insight}</div>
                            <div className="next-step-lbl">Recommended Next Step</div>
                            <div className="next-step">→ {latestAssessment.nextStep}</div>
                          </div>
                        </>
                      ) : <div style={{ color: BRAND.dim, fontSize: 14 }}>No assessments yet.</div>}
                    </div>
                    {dashUser.sessions.length > 0 && (
                      <div className="card">
                        <div className="card-title">Session History</div>
                        {[...dashUser.sessions].reverse().map((s, i) => (
                          <div key={i} className="dash-note">
                            <div className="dash-note-title">{s.title}</div>
                            <div className="dash-note-date">{s.date} · {s.msgCount} exchanges</div>
                            {s.actionItems?.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                {s.actionItems.map((a, j) => <div key={j} className="note-action-item"><span className="note-action-dot">→</span>{a}</div>)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </main>
        </div>
        {managingUser && (
          <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",background:"rgba(0,0,0,0.6)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{background:"#122130",border:"1px solid #2a4455",borderRadius:16,padding:32,width:"100%",maxWidth:420}}>
              <div className="card-title" style={{marginBottom:20}}>Manage Client</div>
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input className="form-input" value={manageForm.firstName || ""} onChange={e => setManageForm(p => ({...p, firstName: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="form-input" value={manageForm.lastName || ""} onChange={e => setManageForm(p => ({...p, lastName: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <input className="form-input" value={manageForm.role || ""} onChange={e => setManageForm(p => ({...p, role: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Company</label>
                <input className="form-input" value={manageForm.company || ""} onChange={e => setManageForm(p => ({...p, company: e.target.value}))} />
              </div>
              <div style={{display:"flex",gap:10,marginTop:20}}>
                <button className="btn-primary" style={{flex:1}} onClick={async () => {
                  const fullName = `${manageForm.firstName} ${manageForm.lastName}`.trim();
                  await supabase.from("profiles").update({ name: fullName, role: manageForm.role, company: manageForm.company }).eq("id", managingUser);
                  await supabase.from("invited_users").update({ name: fullName, role: manageForm.role, company: manageForm.company }).eq("email", managingUser);
                  setUsers(prev => prev[managingUser] ? { ...prev, [managingUser]: { ...prev[managingUser], name: fullName, role: manageForm.role, company: manageForm.company } } : prev);
                  setInvitedUsers(prev => prev.map(u => u.email === managingUser ? { ...u, name: fullName, role: manageForm.role, company: manageForm.company } : u));
                  showToast("Client updated!");
                  setManagingUser(null);
                }}>Save Changes</button>
                <button className="btn-sm" style={{padding:"0 20px"}} onClick={() => setManagingUser(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {toast && <div className="toast">{toast}</div>}
      </>
    );
  }

  // ── MAIN APP ──
  return (
    <>
      <style>{CSS}</style>
      <div className="app">
        <header className="header">
          <div className="logo">
            <LogoMark size={32} />
            <div>
              <div className="logo-text">VANTAGE <span>OpEx</span></div>
              <div className="logo-sub">Executive Performance Platform</div>
            </div>
          </div>
          <div className="header-right">
            {isCoach && <button className="btn-sm" style={{marginRight:4}} onClick={() => setScreen("coach")}>Dashboard</button>}
            <div className="user-badge" style={{cursor:"pointer"}} onClick={() => { setProfileForm({ name: user?.name || "", role: user?.role || "", company: user?.company || "", industry: user?.industry || "", org_size: user?.org_size || "", coach_persona: user?.coach_persona || "Holly", faith_integration: user?.faith_integration || false }); setShowProfileModal(true); }} title="Edit profile">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {user?.name ? user.name.split(" ")[0] : "Profile"}{user?.role ? ` · ${user.role}` : ""}
            </div>
            <button className={`btn-sm ${voiceEnabled ? "active" : ""}`} onClick={() => setVoiceEnabled(v => !v)}>{voiceEnabled ? "Voice On" : "Voice Off"}</button>
            <button className="btn-sm" onClick={async () => { await autoSaveNotes(messages, currentUser); if (messages.filter(m => m.role === "user").length > 0) { saveConversation(currentUser, messages).catch(console.error); } await supabase.auth.signOut(); setScreen("login"); setCurrentUser(null); setMessages([]); setAssessment(null); }}>Sign Out</button>
          </div>
        </header>

        <nav className="nav">
          {[
            ["coach", <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>Coach</>],
            ["book", <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Book</>],
            ["actions", <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>Actions</>],
            ["assessment", <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>Assessment</>],
            ["notes", <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>Notes</>],
            ["scenarios", <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>Scenarios</>],
          ].map(([id, label]) => (
            <button key={id} className={`nav-tab ${activeTab === id ? "active" : ""}`} onClick={() => setActiveTab(id)}>{label}</button>
          ))}
        </nav>

        {activeTab === "coach" && actionItems.filter(a => !a.completed).length > 0 && (
          <div className="action-banner">
            <div className="action-banner-title" onClick={() => setBannerCollapsed(c => !c)}>
              <span>✅ Action items ({actionItems.filter(a => !a.completed).length} pending)</span>
              <span style={{fontSize:12,color:"#26a5cc"}}>{bannerCollapsed ? "▼ Show" : "▲ Hide"}</span>
            </div>
            {!bannerCollapsed && (
              <div className="action-items" style={{marginTop:8}}>
                {actionItems.filter(item => !item.completed).map((item, i) => (
                  <div key={item.id || i} className="action-item" style={{display:"flex",alignItems:"center",gap:10}}>
                    <div onClick={async () => {
                      const updated = !item.completed;
                      await supabase.from("action_items").update({ completed: updated }).eq("id", item.id);
                      setActionItems(prev => prev.map(a => a.id === item.id ? { ...a, completed: updated } : a));
                    }} style={{width:16,height:16,borderRadius:"50%",border:"2px solid #26a5cc",background:"transparent",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
                    </div>
                    <span style={{fontSize:13,color:"#c8d8e0"}}>{item.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <main className="content">

          {activeTab === "coach" && (
            <div className="chat-wrap">
              <div className="chat-msgs">
                {messages.map((m, i) => (
                  <div key={i} className={`msg ${m.role}`}>
                    <div className="msg-lbl">{m.role === "assistant" ? (user?.coach_persona || "Holly") : (user?.name?.split(" ")[0] || "You")}</div>
                    <div className="msg-bubble">
                      {m.role === "assistant" && m.content.includes("📋 Add to action list?")
                        ? <>{m.content.replace("📋 Add to action list?", "").trim()}
                            <ActionPrompt msg={m} messages={messages} currentUser={currentUser} conversationId={conversationId} extractAndSaveActionItems={extractAndSaveActionItems} loadActionItems={loadActionItems} setActionItems={setActionItems} />
                          </>
                        : m.content
                      }
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="msg assistant">
                    <div className="msg-lbl">{user?.coach_persona || "Holly"}</div>
                    <div className="msg-bubble"><div className="typing"><div className="tdot"/><div className="tdot"/><div className="tdot"/></div></div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              {autoSaving && <div className="autosave-bar">✦ Auto-saving session notes...</div>}
              <div className="chat-input">
                <button className={`btn-icon btn-mic ${isListening ? "on" : ""}`} onClick={isListening ? () => { recognitionRef.current?.stop(); setIsListening(false); } : startListening}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </button>
                <input type="file" ref={fileInputRef} style={{display:"none"}} accept="image/*,.pdf,.doc,.docx,.txt" multiple onChange={async e => {
                  const files = Array.from(e.target.files);
                  const newAttachments = await Promise.all(files.map(f => new Promise(resolve => {
                    const reader = new FileReader();
                    reader.onload = () => resolve({ name: f.name, type: f.type, data: reader.result });
                    reader.readAsDataURL(f);
                  })));
                  setAttachments(prev => [...prev, ...newAttachments]);
                  e.target.value = "";
                }} />
                <button className="btn-icon btn-mic" onClick={() => fileInputRef.current?.click()} title="Attach file">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
                  {attachments.length > 0 && (
                    <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"4px 0"}}>
                      {attachments.map((a, i) => (
                        <div key={i} style={{background:"rgba(38,165,204,0.1)",border:"1px solid rgba(38,165,204,0.25)",borderRadius:6,padding:"3px 8px",fontSize:11,color:"#72bfd0",display:"flex",alignItems:"center",gap:6}}>
                          📄 {a.name}
                          <span style={{cursor:"pointer",color:"#e57373"}} onClick={() => setAttachments(prev => prev.filter((_,j) => j !== i))}>×</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea className="chat-ta" placeholder="Describe your leadership challenge..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} rows={1} />
                </div>
                <button className="btn-assess" onClick={runAssessment}>Assess</button>
                {user?.faith_integration && (
                  <button className="btn-assess" style={{background:"rgba(147,112,219,0.15)",borderColor:"rgba(147,112,219,0.3)",color:"#b39ddb",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={() => { setBibleStudy(null); setShowBiblePanel(p => !p); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><line x1="4" y1="8" x2="20" y2="8"/></svg>
                </button>
                )}
                <button className="btn-icon btn-send" onClick={sendMessage} disabled={loading || (!input.trim() && attachments.length === 0)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </div>
            </div>
          )}

          {activeTab === "book" && (
            <div style={{maxWidth:580,margin:"0 auto",padding:"28px 20px 80px"}}>

              {/* ── UPCOMING SESSIONS ── */}
              {upcomingSessions.length > 0 && (
                <div style={{marginBottom:28}}>
                  <div style={{fontSize:10,letterSpacing:"0.14em",textTransform:"uppercase",color:"rgba(56,182,216,0.6)",fontFamily:"Space Grotesk,sans-serif",fontWeight:600,marginBottom:12}}>Upcoming Sessions</div>
                  {upcomingSessions.map((s, i) => (
                    <div key={i} style={{padding:"16px 20px",background:"linear-gradient(135deg,rgba(56,182,216,0.08),rgba(30,60,100,0.12))",border:"1px solid rgba(56,182,216,0.2)",borderRadius:12,marginBottom:8,display:"flex",alignItems:"center",gap:16}}>
                      <div style={{width:42,height:42,borderRadius:10,background:"rgba(56,182,216,0.12)",border:"1px solid rgba(56,182,216,0.2)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38b6d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </div>
                      <div style={{flex:1}}>
                        <div style={{color:"#e8f0f4",fontSize:14,fontWeight:600,fontFamily:"Montserrat,sans-serif"}}>{new Date(s.start_time).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
                        <div style={{color:"rgba(56,182,216,0.8)",fontSize:12,marginTop:3,fontFamily:"Space Grotesk,sans-serif"}}>{new Date(s.start_time).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})} · {s.duration_minutes}min · ${s.amount_cents/100}</div>
                      </div>
                      <div style={{fontSize:11,color:"rgba(168,220,232,0.6)",fontFamily:"Space Grotesk,sans-serif",letterSpacing:"0.05em"}}>with Holly</div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── STEP: DATE ── */}
              {bookingStep === "date" && (
                <div>
                  {/* Coach card */}
                  <div style={{background:"linear-gradient(135deg,rgba(18,36,62,0.95),rgba(10,22,40,0.98))",border:"1px solid rgba(56,182,216,0.2)",borderRadius:16,padding:"24px",marginBottom:24,display:"flex",alignItems:"center",gap:18,boxShadow:"0 0 40px rgba(56,182,216,0.06),inset 0 1px 0 rgba(56,182,216,0.08)"}}>
                    <div style={{width:56,height:56,borderRadius:14,background:"linear-gradient(135deg,rgba(56,182,216,0.25),rgba(30,100,160,0.2))",border:"1px solid rgba(56,182,216,0.3)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"none"}}><LogoMark size={34} /></div>
                    <div>
                      <div style={{color:"#e8f0f4",fontSize:17,fontWeight:700,fontFamily:"Montserrat,sans-serif",letterSpacing:"-0.01em"}}>Holly Silva</div>
                      <div style={{color:"rgba(56,182,216,0.8)",fontSize:12,marginTop:3,fontFamily:"Space Grotesk,sans-serif",letterSpacing:"0.04em"}}>Executive Coach · Warm, direct, intuitive</div>
                      <div style={{color:"rgba(168,220,232,0.5)",fontSize:11,marginTop:6,fontFamily:"Space Grotesk,sans-serif"}}>Available Mon · Wed · Fri, 10am – 5:30pm ET</div>
                    </div>
                  </div>

                  <div style={{fontSize:10,letterSpacing:"0.14em",textTransform:"uppercase",color:"rgba(56,182,216,0.6)",fontFamily:"Space Grotesk,sans-serif",fontWeight:600,marginBottom:12}}>Select a Date</div>
                  <input className="form-input" type="date" value={bookingDate}
                    min={new Date().toISOString().split("T")[0]}
                    style={{marginBottom:20,fontSize:15}}
                    onChange={e => setBookingDate(e.target.value)} />
                  {bookingError && <div style={{color:"#e57373",fontSize:13,marginBottom:12}}>{bookingError}</div>}
                  <button className="btn-primary" style={{width:"100%",padding:"15px",fontSize:13,background:"linear-gradient(135deg,#3ec8e8,#1fa8cc)",boxShadow:"0 4px 24px rgba(56,182,216,0.3)"}} disabled={!bookingDate || bookingLoading}
                    onClick={async () => {
                      setBookingLoading(true); setBookingError("");
                      try {
                        const res = await fetch(`/api/calendar?action=slots&persona=${user?.coach_persona || "holly"}&date=${bookingDate}`);
                        const data = await res.json();
                        if (!data.slots || data.slots.length === 0) { setBookingError("No availability on that date. Try Monday, Wednesday, or Friday."); }
                        else { setBookingSlots(data.slots); setBookingStep("slots"); }
                      } catch(e) { setBookingError("Could not load slots. Please try again."); }
                      setBookingLoading(false);
                    }}>{bookingLoading ? "Checking availability..." : "See Available Times →"}</button>
                </div>
              )}

              {/* ── STEP: SLOTS ── */}
              {bookingStep === "slots" && (
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}>
                    <button onClick={() => { setBookingStep("date"); setBookingSlot(null); setBookingDuration(null); }} style={{background:"transparent",border:"1px solid rgba(56,182,216,0.2)",borderRadius:8,color:"rgba(168,220,232,0.7)",padding:"6px 14px",cursor:"pointer",fontSize:12,fontFamily:"Space Grotesk,sans-serif",letterSpacing:"0.05em"}}>← Back</button>
                    <div>
                      <div style={{color:"#e8f0f4",fontSize:14,fontWeight:600,fontFamily:"Montserrat,sans-serif"}}>{new Date(bookingDate + "T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
                      <div style={{color:"rgba(56,182,216,0.7)",fontSize:11,fontFamily:"Space Grotesk,sans-serif",marginTop:2}}>with Holly · Select a time</div>
                    </div>
                  </div>

                  {/* Group by morning/afternoon */}
                  {["Morning","Afternoon","Evening"].map(period => {
                    const periodSlots = bookingSlots.filter(slot => {
                      const h = new Date(slot.start).getHours();
                      if (period === "Morning") return h >= 6 && h < 12;
                      if (period === "Afternoon") return h >= 12 && h < 17;
                      return h >= 17;
                    });
                    if (!periodSlots.length) return null;
                    return (
                      <div key={period} style={{marginBottom:24}}>
                        <div style={{fontSize:10,letterSpacing:"0.14em",textTransform:"uppercase",color:"rgba(56,182,216,0.45)",fontFamily:"Space Grotesk,sans-serif",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                          <span>{period}</span>
                          <div style={{flex:1,height:1,background:"rgba(56,182,216,0.1)"}}/>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {periodSlots.map((slot, i) => {
                            const time = new Date(slot.start).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
                            const sel30 = bookingSlot?.start === slot.start && bookingDuration === 30;
                            const sel60 = bookingSlot?.start === slot.start && bookingDuration === 60;
                            return (
                              <div key={i} style={{background:"linear-gradient(135deg,rgba(14,26,46,0.9),rgba(8,16,32,0.95))",border:`1px solid ${(sel30||sel60) ? "rgba(56,182,216,0.4)" : "rgba(56,182,216,0.1)"}`,borderRadius:12,padding:"14px 18px",transition:"all 0.2s",boxShadow:(sel30||sel60) ? "0 0 20px rgba(56,182,216,0.1)" : "none"}}>
                                <div style={{color:"#e8f0f4",fontSize:15,fontWeight:700,fontFamily:"Montserrat,sans-serif",marginBottom:10}}>{time}</div>
                                <div style={{display:"flex",gap:8}}>
                                  {slot.available30 && (
                                    <button onClick={() => { setBookingSlot(slot); setBookingDuration(30); }}
                                      style={{flex:1,padding:"10px 0",borderRadius:8,border:`1px solid ${sel30 ? "rgba(56,182,216,0.6)" : "rgba(56,182,216,0.15)"}`,background:sel30 ? "linear-gradient(135deg,rgba(56,182,216,0.25),rgba(30,100,160,0.2))" : "rgba(56,182,216,0.04)",color:sel30 ? "#38b6d8" : "#c8dce6",cursor:"pointer",fontSize:12,fontFamily:"Space Grotesk,sans-serif",fontWeight:600,letterSpacing:"0.04em",transition:"all 0.2s",boxShadow:sel30 ? "0 0 12px rgba(56,182,216,0.15)" : "none"}}>
                                      30 MIN · $75
                                    </button>
                                  )}
                                  {slot.available60 && (
                                    <button onClick={() => { setBookingSlot(slot); setBookingDuration(60); }}
                                      style={{flex:1,padding:"10px 0",borderRadius:8,border:`1px solid ${sel60 ? "rgba(56,182,216,0.6)" : "rgba(56,182,216,0.15)"}`,background:sel60 ? "linear-gradient(135deg,rgba(56,182,216,0.25),rgba(30,100,160,0.2))" : "rgba(56,182,216,0.04)",color:sel60 ? "#38b6d8" : "#c8dce6",cursor:"pointer",fontSize:12,fontFamily:"Space Grotesk,sans-serif",fontWeight:600,letterSpacing:"0.04em",transition:"all 0.2s",boxShadow:sel60 ? "0 0 12px rgba(56,182,216,0.15)" : "none"}}>
                                      60 MIN · $150
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {bookingError && <div style={{color:"#e57373",fontSize:13,marginBottom:12}}>{bookingError}</div>}

                  {/* Sticky continue button */}
                  {bookingSlot && bookingDuration && (
                    <div style={{position:"fixed",bottom:0,left:0,right:0,padding:"16px 24px",background:"rgba(8,15,26,0.95)",borderTop:"1px solid rgba(56,182,216,0.12)",backdropFilter:"blur(12px)",zIndex:50}}>
                      <div style={{maxWidth:580,margin:"0 auto"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                          <div style={{color:"#e8f0f4",fontSize:13,fontFamily:"Montserrat,sans-serif",fontWeight:600}}>
                            {new Date(bookingSlot.start).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})} · {bookingDuration}min
                          </div>
                          <div style={{color:"#38b6d8",fontSize:16,fontWeight:700,fontFamily:"Montserrat,sans-serif"}}>${bookingDuration === 30 ? 75 : 150}</div>
                        </div>
                        <button className="btn-primary" style={{width:"100%",padding:"14px",fontSize:13}} disabled={bookingLoading}
                          onClick={async () => {
                            setBookingLoading(true); setBookingError("");
                            try {
                              const { data: { session: authSession } } = await supabase.auth.getSession();
                              const res = await fetch("/api/booking?action=create-intent", {
                                method: "POST", headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ duration: bookingDuration, persona: user?.coach_persona || "holly", start: bookingSlot.start, clientId: authSession.user.id }),
                              });
                              const data = await res.json();
                              if (data.error) throw new Error(data.error);
                              setClientSecret(data.clientSecret); setBookingSessionId(data.sessionId); setBookingStep("payment");
                            } catch(e) { setBookingError(e.message); }
                            setBookingLoading(false);
                          }}>{bookingLoading ? "Preparing..." : "Continue to Payment →"}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP: PAYMENT ── */}
              {bookingStep === "payment" && clientSecret && (() => {
                setTimeout(() => {
                  if (window.Stripe && !window._stripeCardElement) {
                    window._stripeInstance = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
                    const elements = window._stripeInstance.elements({ clientSecret });
                    const card = elements.create("card", { style: { base: { color: "#e0eef5", fontSize: "15px", "::placeholder": { color: "#3a5a6e" } } } });
                    card.mount("#stripe-card-element");
                    window._stripeCardElement = card;
                    window._stripeElements = elements;
                  }
                }, 100);
                return null;
              })()}
              {bookingStep === "payment" && clientSecret && (
                <div>
                  <button onClick={() => { if (window._stripeCardElement) { window._stripeCardElement.destroy(); window._stripeCardElement = null; } setBookingStep("slots"); }} style={{background:"transparent",border:"1px solid rgba(56,182,216,0.2)",borderRadius:8,color:"rgba(168,220,232,0.7)",padding:"6px 14px",cursor:"pointer",fontSize:12,fontFamily:"Space Grotesk,sans-serif",marginBottom:20}}>← Back</button>

                  {/* Booking summary */}
                  <div style={{background:"linear-gradient(135deg,rgba(56,182,216,0.08),rgba(30,60,100,0.1))",border:"1px solid rgba(56,182,216,0.2)",borderRadius:12,padding:"18px 20px",marginBottom:20}}>
                    <div style={{fontSize:10,letterSpacing:"0.14em",textTransform:"uppercase",color:"rgba(56,182,216,0.6)",fontFamily:"Space Grotesk,sans-serif",fontWeight:600,marginBottom:10}}>Booking Summary</div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{color:"#e8f0f4",fontSize:15,fontWeight:700,fontFamily:"Montserrat,sans-serif"}}>{new Date(bookingSlot.start).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
                        <div style={{color:"rgba(168,220,232,0.7)",fontSize:12,marginTop:4,fontFamily:"Space Grotesk,sans-serif"}}>{new Date(bookingSlot.start).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})} · {bookingDuration}min with Holly</div>
                      </div>
                      <div style={{color:"#38b6d8",fontSize:22,fontWeight:800,fontFamily:"Montserrat,sans-serif"}}>${bookingDuration === 30 ? 75 : 150}</div>
                    </div>
                  </div>

                  <div style={{fontSize:10,letterSpacing:"0.14em",textTransform:"uppercase",color:"rgba(56,182,216,0.6)",fontFamily:"Space Grotesk,sans-serif",fontWeight:600,marginBottom:10}}>Payment Details</div>
                  <div id="stripe-card-element" style={{padding:"14px 16px",background:"rgba(16,28,48,0.8)",border:"1px solid rgba(56,182,216,0.2)",borderRadius:10,marginBottom:16}}></div>
                  {bookingError && <div style={{color:"#e57373",fontSize:13,marginBottom:12}}>{bookingError}</div>}
                  <button className="btn-primary" style={{width:"100%",padding:"15px",fontSize:13}} disabled={bookingLoading}
                    onClick={async () => {
                      setBookingLoading(true); setBookingError("");
                      try {
                        const stripe = window._stripeInstance || window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
                        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: window._stripeCardElement } });
                        if (error) throw new Error(error.message);
                        await fetch("/api/booking?action=confirm", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ paymentIntentId: paymentIntent.id }) });
                        const { data } = await supabase.from("coaching_sessions").select("*").eq("id", bookingSessionId).single();
                        if (data) setUpcomingSessions(prev => [...prev, data]);
                        setBookingStep("confirm");
                      } catch(e) { setBookingError(e.message); }
                      setBookingLoading(false);
                    }}>{bookingLoading ? "Processing payment..." : `Pay $${bookingDuration === 30 ? 75 : 150} →`}</button>
                </div>
              )}

              {/* ── STEP: CONFIRM ── */}
              {bookingStep === "confirm" && (
                <div style={{textAlign:"center",padding:"48px 20px"}}>
                  <div style={{width:72,height:72,borderRadius:"50%",background:"linear-gradient(135deg,rgba(56,182,216,0.2),rgba(30,100,160,0.15))",border:"1px solid rgba(56,182,216,0.3)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 24px",boxShadow:"0 0 40px rgba(56,182,216,0.15)"}}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#38b6d8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div style={{color:"#e8f0f4",fontSize:22,fontWeight:700,fontFamily:"Montserrat,sans-serif",marginBottom:8,letterSpacing:"-0.01em"}}>Session Booked!</div>
                  <div style={{color:"rgba(168,220,232,0.7)",fontSize:13,marginBottom:8,fontFamily:"Space Grotesk,sans-serif"}}>A calendar invite has been sent to your email.</div>
                  <div style={{color:"rgba(56,182,216,0.8)",fontSize:13,marginBottom:32,fontFamily:"Space Grotesk,sans-serif"}}>{bookingSlot && new Date(bookingSlot.start).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} · {bookingSlot && new Date(bookingSlot.start).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</div>
                  <button className="btn-primary" onClick={() => { setBookingStep("date"); setBookingDate(""); setBookingSlot(null); setBookingDuration(null); setClientSecret(null); }}>Book Another Session</button>
                </div>
              )}
            </div>
          )}
          {activeTab === "assessment" && (
            <>
              {assessmentLoading && <div className="spinner-wrap"><div className="spinner"/>Analyzing your leadership profile...</div>}
              {!assessmentLoading && !assessment && (
                <div className="card">
                  <div className="empty-state">
                    <div className="empty-icon">📊</div>
                    <div className="empty-text">Have a coaching conversation, then click <strong style={{color:BRAND.blue}}>"Assess"</strong> to generate your leadership profile.</div>
                    <button className="btn-primary" onClick={() => setActiveTab("coach")}>Start Session →</button>
                  </div>
                </div>
              )}
              {!assessmentLoading && assessment && (
                <div className="grid-2">
                  <div className="card">
                    <div className="card-title">Leadership Radar</div>
                    <div className="radar-center"><RadarChart scores={assessment.scores} /></div>
                  </div>
                  <div className="card">
                    <div className="card-title">Strengths</div>
                    <div className="tag-list">{assessment.strengths?.map((s,i) => <div key={i} className="tag">✦ {s}</div>)}</div>
                    <div className="card-title" style={{marginTop:18}}>Growth Areas</div>
                    <div className="tag-list">{assessment.growthAreas?.map((g,i) => <div key={i} className="tag growth">→ {g}</div>)}</div>
                  </div>
                  <div className="card col-span-2">
                    <div className="card-title">Coaching Insight</div>
                    <div className="insight-text">{assessment.insight}</div>
                    <div className="next-step-lbl">Your Next Step</div>
                    <div className="next-step">→ {assessment.nextStep}</div>
                  </div>
                  {user?.assessments.length > 1 && (
                    <div className="card col-span-2">
                      <div className="card-title">Progress Over Time</div>
                      <div className="progress-chart">
                        {SCORE_KEYS.map(k => {
                          const all = user.assessments.map(a => a.scores[k] || 0);
                          const latest = all[all.length - 1];
                          const diff = latest - all[0];
                          return (
                            <div key={k} className="score-row">
                              <div className="score-label">{k}</div>
                              <div className="score-bar-wrap"><div className="score-bar" style={{width:`${latest*10}%`}}/></div>
                              <div className="score-val">{latest}</div>
                              <div className={diff>0?"trend-up":diff<0?"trend-down":"score-val"}>{diff>0?`+${diff}`:diff<0?diff:"—"}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === "actions" && (
            <div className="card" style={{position:"sticky",top:0}}>
              <div className="card-title">Action Items</div>
              {actionItems.length === 0 && (
                <div style={{color:"#3a5a6e",fontSize:13,padding:"16px 0"}}>No action items yet. They'll appear here as you work through sessions.</div>
              )}
              {actionItems.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:8}}>
                  {actionItems.map((item, i) => (
                    <div key={item.id || i} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"12px 16px",background:"rgba(255,255,255,0.03)",borderRadius:8,border:`1px solid ${item.completed ? "rgba(76,175,80,0.2)" : "rgba(38,165,204,0.15)"}`}}>
                      <div onClick={async () => {
                          const updated = !item.completed;
                          await supabase.from("action_items").update({ completed: updated }).eq("id", item.id);
                          setActionItems(prev => prev.map(a => a.id === item.id ? { ...a, completed: updated } : a));
                        }} style={{width:20,height:20,borderRadius:"50%",border:`2px solid ${item.completed ? "#4caf50" : "#26a5cc"}`,background: item.completed ? "#4caf50" : "transparent",cursor:"pointer",flexShrink:0,marginTop:2,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
                        {item.completed && <span style={{color:"#fff",fontSize:12,fontWeight:"bold"}}>✓</span>}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:14,color: item.completed ? "#4a7a5e" : "#e8e8e8",textDecoration: item.completed ? "line-through" : "none",lineHeight:1.5}}>{item.text}</div>
                        {item.due_date && (
                          <div style={{fontSize:11,color:"#72bfd0",marginTop:4,fontFamily:"Montserrat,sans-serif"}}>
                            Due: {new Date(item.due_date).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
                          </div>
                        )}
                      </div>
                      <button onClick={async () => {
                        await supabase.from("action_items").delete().eq("id", item.id);
                        setActionItems(prev => prev.filter(a => a.id !== item.id));
                      }} style={{background:"none",border:"none",color:"#3a5a6e",cursor:"pointer",fontSize:16,padding:"0 4px",flexShrink:0}}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {actionItems.filter(a => !a.completed).length > 0 && (
                <div style={{marginTop:16,fontSize:12,color:"#3a5a6e",fontFamily:"Montserrat,sans-serif"}}>
                  {actionItems.filter(a => !a.completed).length} pending · {actionItems.filter(a => a.completed).length} completed
                </div>
              )}
            </div>
          )}

          {activeTab === "notes" && (
            <div className="card">
              <div className="card-title">Session Notes</div>
              {user?.sessions.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">📝</div>
                  <div className="empty-text">No session notes yet. Notes are saved automatically during your coaching conversations.</div>
                  <button className="btn-primary" onClick={() => setActiveTab("coach")}>Go to Coach →</button>
                </div>
              )}
              {user?.sessions.length > 0 && (
                <div className="notes-list">
                  {[...user.sessions].map((s, i) => (
                    <div key={i} className="note-card">
                      <div className="note-header">
                        <div className="note-title">{s.title}</div>
                        <div className="note-date">{s.date}</div>
                      </div>
                      <div className="note-summary">{s.summary}</div>
                      {s.insights?.length > 0 && (
                        <>
                          <div className="note-section-lbl">Key Insights</div>
                          <div style={{marginBottom:10}}>{s.insights.map((ins,j) => <div key={j} className="note-item"><span className="note-item-dot">✦</span>{ins}</div>)}</div>
                        </>
                      )}
                      {s.actionItems?.length > 0 && (
                        <>
                          <div className="note-section-lbl">Action Items</div>
                          {s.actionItems.map((a,j) => <div key={j} className="note-action-item"><span className="note-action-dot">→</span>{a}</div>)}
                        </>
                      )}
                      <button className="btn-primary" style={{marginTop:14,fontSize:13,padding:"9px 18px"}} onClick={() => { setActiveTab("coach"); setTimeout(() => runAssessment(), 100); }}>📊 Assess This Session</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "scenarios" && (
            <>
              <div className="scenarios-grid">
                {SCENARIOS.map(s => (
                  <div key={s.id} className={`sc-card ${activeScenario?.id === s.id ? "active" : ""}`}
                    onClick={() => { setActiveScenario(s); setScenarioResponse(""); setScenarioFeedback(""); }}>
                    <div className="sc-icon">{s.icon}</div>
                    <div className="sc-cat">{s.category}</div>
                    <div className="sc-title">{s.title}</div>
                    <div className="sc-desc">{s.description}</div>
                  </div>
                ))}
              </div>
              {activeScenario && (
                <div className="sc-workspace">
                  <div className="card-title">{activeScenario.icon} {activeScenario.title}</div>
                  <div className="sc-prompt">{activeScenario.description}</div>
                  <textarea className="sc-ta" placeholder="How do you handle this? Walk through your thinking and actions..." value={scenarioResponse} onChange={e => setScenarioResponse(e.target.value)} />
                  <button className="btn-primary" onClick={submitScenario} disabled={scenarioLoading || !scenarioResponse.trim()}>
                    {scenarioLoading ? "Analyzing..." : "Get Coaching Feedback →"}
                  </button>
                  {scenarioFeedback && <div className="feedback-box"><div className="fb-lbl">Performance Feedback</div>{scenarioFeedback}</div>}
                </div>
              )}
            </>
          )}

        </main>
      </div>
      {toast && <div className="toast">{toast}</div>}

      {showOnboarding && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",background:"rgba(0,0,0,0.85)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#0a1628",border:"1px solid rgba(38,165,204,0.2)",borderRadius:16,padding:40,width:"100%",maxWidth:480,margin:"0 16px"}}>
            <div style={{textAlign:"center",marginBottom:32}}>
              <LogoMark size={44} />
              <div style={{marginTop:16,fontSize:11,letterSpacing:3,color:"#26a5cc",fontFamily:"Montserrat,sans-serif",textTransform:"uppercase"}}>Welcome to Vantage OpEx</div>
            </div>
            <h2 style={{fontSize:18,fontWeight:500,color:"#fff",marginBottom:8,fontFamily:"Montserrat,sans-serif",textAlign:"center"}}>Choose your coach</h2>
            <p style={{fontSize:13,color:"#888",marginBottom:28,textAlign:"center",lineHeight:1.7,fontFamily:"Montserrat,sans-serif"}}>Your coach will guide your sessions, remember your goals, and grow with you over time. You can change this anytime in your profile.</p>
            <div style={{display:"flex",gap:16,marginBottom:28}}>
              {["Holly","David"].map(persona => (
                <div key={persona} onClick={() => setSelectedPersona(persona)} style={{flex:1,padding:"20px 16px",borderRadius:12,border:`2px solid ${selectedPersona === persona ? "#26a5cc" : "rgba(255,255,255,0.08)"}`,background: selectedPersona === persona ? "rgba(38,165,204,0.1)" : "rgba(255,255,255,0.02)",cursor:"pointer",textAlign:"center",transition:"all 0.2s"}}>
                  <div style={{fontSize:32,marginBottom:8}}>{persona === "Holly" ? "👩" : "👨"}</div>
                  <div style={{fontSize:15,fontWeight:600,color:"#fff",fontFamily:"Montserrat,sans-serif",marginBottom:4}}>{persona}</div>
                  <div style={{fontSize:11,color:"#888",fontFamily:"Montserrat,sans-serif"}}>{persona === "Holly" ? "Warm, direct, intuitive" : "Strategic, grounded, precise"}</div>
                </div>
              ))}
            </div>
            <div style={{marginBottom:24}}>
              <div style={{fontSize:12,color:"#72bfd0",marginBottom:10,fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Faith Integration</div>
              <div onClick={() => setShowOnboarding(prev => prev)} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderRadius:10,border:"1px solid rgba(255,255,255,0.08)",background:"rgba(255,255,255,0.02)",cursor:"pointer"}}
                onClick={async () => {
                  const newVal = !(user?.faith_integration);
                  await supabase.from("profiles").update({ faith_integration: newVal }).eq("id", currentUser);
                  setUsers(prev => ({ ...prev, [currentUser]: { ...prev[currentUser], faith_integration: newVal } }));
                }}>
                <div style={{width:20,height:20,borderRadius:"50%",border:`2px solid ${user?.faith_integration ? "#26a5cc" : "rgba(255,255,255,0.2)"}`,background: user?.faith_integration ? "#26a5cc" : "transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {user?.faith_integration && <span style={{color:"#fff",fontSize:11}}>✓</span>}
                </div>
                <div>
                  <div style={{fontSize:13,color:"#e8e8e8",fontFamily:"Montserrat,sans-serif"}}>Include biblical wisdom</div>
                  <div style={{fontSize:11,color:"#888",fontFamily:"Montserrat,sans-serif",marginTop:2}}>Your coach will offer scripture-based insights when relevant</div>
                </div>
              </div>
            </div>
            <button onClick={async () => {
              await supabase.from("profiles").update({ coach_persona: selectedPersona }).eq("id", currentUser);
              setUsers(prev => ({ ...prev, [currentUser]: { ...prev[currentUser], coach_persona: selectedPersona } }));
              setShowOnboarding(false);
            }} style={{width:"100%",padding:"14px",background:"#26a5cc",border:"none",borderRadius:8,color:"#fff",fontSize:15,cursor:"pointer",fontFamily:"Montserrat,sans-serif",fontWeight:600,letterSpacing:1}}>
              Begin with {selectedPersona} →
            </button>
          </div>
        </div>
      )}

      {showBiblePanel && (
        <div style={{position:"fixed",top:0,right:0,width:"100%",maxWidth:420,height:"100%",background:"#0a1628",borderLeft:"1px solid rgba(147,112,219,0.2)",zIndex:300,display:"flex",flexDirection:"column",overflowY:"auto"}}>
          <div style={{padding:"24px 24px 16px",borderBottom:"1px solid rgba(147,112,219,0.15)",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:"#0a1628"}}>
            <div>
              <div style={{fontSize:11,letterSpacing:2,color:"#b39ddb",fontFamily:"Montserrat,sans-serif",textTransform:"uppercase"}}>Biblical Wisdom</div>
              <div style={{fontSize:16,color:"#fff",fontFamily:"Montserrat,sans-serif",marginTop:4,fontWeight:500}}>Scripture Study</div>
            </div>
            <button onClick={() => setShowBiblePanel(false)} style={{background:"none",border:"none",color:"#888",fontSize:24,cursor:"pointer"}}>×</button>
          </div>
          {!bibleStudy && !bibleLoading && (
            <div style={{padding:24,display:"flex",flexDirection:"column",gap:12}}>
              <p style={{fontSize:13,color:"#888",lineHeight:1.8,fontFamily:"Montserrat,sans-serif"}}>Explore what Scripture says about your current leadership challenge.</p>
              <button onClick={() => runBibleStudy("quick")} style={{padding:"16px 20px",background:"rgba(147,112,219,0.08)",border:"1px solid rgba(147,112,219,0.25)",borderRadius:12,color:"#b39ddb",fontSize:14,cursor:"pointer",fontFamily:"Montserrat,sans-serif",textAlign:"left"}}>
                <div style={{fontWeight:600,marginBottom:4}}>⚡ Quick Study</div>
                <div style={{fontSize:12,color:"#666"}}>3-5 minutes · Focused on your current challenge</div>
              </button>
              <button onClick={() => runBibleStudy("deep")} style={{padding:"16px 20px",background:"rgba(147,112,219,0.08)",border:"1px solid rgba(147,112,219,0.25)",borderRadius:12,color:"#b39ddb",fontSize:14,cursor:"pointer",fontFamily:"Montserrat,sans-serif",textAlign:"left"}}>
                <div style={{fontWeight:600,marginBottom:4}}>📖 Deep Dive</div>
                <div style={{fontSize:12,color:"#666"}}>10-15 minutes · Broader biblical leadership principles</div>
              </button>
            </div>
          )}
          {bibleLoading && (
            <div style={{padding:40,display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
              <div className="spinner"/>
              <div style={{fontSize:13,color:"#888",fontFamily:"Montserrat,sans-serif"}}>Searching Scripture...</div>
            </div>
          )}
          {bibleStudy && !bibleLoading && (
            <div style={{padding:24,display:"flex",flexDirection:"column",gap:16}}>
              <div style={{fontSize:11,color:"#b39ddb",fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Topic: {bibleStudy.topic}</div>
              {bibleStudy.verses?.map((v, i) => (
                <div key={i} style={{padding:"14px 16px",background:"rgba(147,112,219,0.06)",borderRadius:10,border:"1px solid rgba(147,112,219,0.15)"}}>
                  <div style={{fontSize:11,color:"#b39ddb",fontFamily:"Montserrat,sans-serif",marginBottom:6,letterSpacing:1}}>{v.book} {v.chapter}:{v.verse}</div>
                  <div style={{fontSize:13,color:"#e8e8e8",lineHeight:1.8,fontStyle:"italic"}}>"{v.text}"</div>
                </div>
              ))}
              {bibleStudy.insight && (
                <div style={{padding:16,background:"rgba(255,255,255,0.03)",borderRadius:10,border:"1px solid rgba(255,255,255,0.06)"}}>
                  <div style={{fontSize:11,color:"#b39ddb",fontFamily:"Montserrat,sans-serif",letterSpacing:1,marginBottom:10,textTransform:"uppercase"}}>Reflection</div>
                  <div style={{fontSize:13,color:"#c8d8e0",lineHeight:1.9,whiteSpace:"pre-wrap"}}>{bibleStudy.insight}</div>
                </div>
              )}
              <button onClick={() => setBibleStudy(null)} style={{padding:10,background:"rgba(147,112,219,0.08)",border:"1px solid rgba(147,112,219,0.2)",borderRadius:8,color:"#b39ddb",fontSize:12,cursor:"pointer",fontFamily:"Montserrat,sans-serif"}}>← New Study</button>
            </div>
          )}
        </div>
      )}

      {showProfileModal && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#0f1e2e",border:"1px solid rgba(38,165,204,0.2)",borderRadius:12,padding:32,width:"100%",maxWidth:400,margin:"0 16px"}}>
            <div style={{fontSize:16,fontWeight:600,color:"#fff",marginBottom:24,fontFamily:"Montserrat,sans-serif",letterSpacing:1}}>Edit Profile</div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div>
                <div style={{fontSize:11,color:"#72bfd0",marginBottom:6,fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Full Name</div>
                <input className="form-input" value={profileForm.name} onChange={e => setProfileForm(p => ({...p, name: e.target.value}))} placeholder="Your full name" />
              </div>
              <div>
                <div style={{fontSize:11,color:"#72bfd0",marginBottom:6,fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Role / Title</div>
                <input className="form-input" value={profileForm.role} onChange={e => setProfileForm(p => ({...p, role: e.target.value}))} placeholder="e.g. CEO, VP of Sales" />
              </div>
              <div>
                <div style={{fontSize:11,color:"#72bfd0",marginBottom:6,fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Company</div>
                <input className="form-input" value={profileForm.company} onChange={e => setProfileForm(p => ({...p, company: e.target.value}))} placeholder="Your company name" />
              </div>
              <div>
                <div style={{fontSize:11,color:"#72bfd0",marginBottom:6,fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Industry</div>
                <input className="form-input" value={profileForm.industry} onChange={e => setProfileForm(p => ({...p, industry: e.target.value}))} placeholder="e.g. Technology, Healthcare, Finance" />
              </div>
              <div>
                <div style={{fontSize:11,color:"#72bfd0",marginBottom:6,fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Organization Size</div>
                <select className="form-input" value={profileForm.org_size} onChange={e => setProfileForm(p => ({...p, org_size: e.target.value}))} style={{background:"#0a1628",color:"#e8e8e8"}}>
                  <option value="">Select size...</option>
                  <option value="Solo / Founder">Solo / Founder</option>
                  <option value="2-10">2–10 employees</option>
                  <option value="11-50">11–50 employees</option>
                  <option value="51-200">51–200 employees</option>
                  <option value="201-1000">201–1,000 employees</option>
                  <option value="1000+">1,000+ employees</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:"#72bfd0",marginBottom:6,fontFamily:"Montserrat,sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Coach Persona</div>
                <select className="form-input" value={profileForm.coach_persona || "Holly"} onChange={e => setProfileForm(p => ({...p, coach_persona: e.target.value}))} style={{background:"#0a1628",color:"#e8e8e8"}}>
                  <option value="Holly">Holly — Warm, direct, intuitive</option>
                  <option value="David">David — Strategic, grounded, precise</option>
                </select>
              </div>
              <div onClick={() => setProfileForm(p => ({...p, faith_integration: !p.faith_integration}))} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderRadius:10,border:"1px solid rgba(255,255,255,0.08)",background:"rgba(255,255,255,0.02)",cursor:"pointer"}}>
                <div style={{width:20,height:20,borderRadius:"50%",border:`2px solid ${profileForm.faith_integration ? "#26a5cc" : "rgba(255,255,255,0.2)"}`,background: profileForm.faith_integration ? "#26a5cc" : "transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {profileForm.faith_integration && <span style={{color:"#fff",fontSize:11}}>✓</span>}
                </div>
                <div>
                  <div style={{fontSize:13,color:"#e8e8e8",fontFamily:"Montserrat,sans-serif"}}>Include biblical wisdom</div>
                  <div style={{fontSize:11,color:"#888",fontFamily:"Montserrat,sans-serif",marginTop:2}}>Your coach will offer scripture-based insights when relevant</div>
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:24,justifyContent:"flex-end"}}>
              <button className="btn-sm" onClick={() => setShowProfileModal(false)}>Cancel</button>
              <button className="btn-sm" style={{background:"#26a5cc",color:"#fff",borderColor:"#26a5cc"}} onClick={handleSaveProfile} disabled={profileSaving}>{profileSaving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
